"use client";

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  useForm,
  ControllerRenderProps,
  FieldErrors,
  useWatch,
} from "react-hook-form";
import * as z from "zod";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import type {
  QMProduct,
  BorrowerPaidDetail,
  AdjustmentGroup,
} from "../lib/types.ts";
import { Download, Info, Calculator } from "lucide-react";
import { SharePopup } from "../components/share-popup.tsx";
import { debug } from "../lib/debug.ts";
import {
  getPrograms,
  getGroupedPrograms,
  getIncomeTypes,
  getAmortizationTypes,
  getCitizenshipOptions,
  getOccupancyOptions,
  getPurposeOptions,
  getPropertyTypeOptions,
  getNumberOfUnitsOptions,
  getDtiOptions,
  getLockPeriodOptions,
  getBrokerCompensationRange,
  getLoanAmountRange,
  getCreditEventOptions,
  getPrepaymentPenaltyOptions,
  getLoanTypes,
  getVestingOptions,
  getMortgageHistoryOptions,
  programConfig,
} from "../lib/program-config.ts";
import { getStateOptions } from "../lib/state-config.ts";
import { fetchPricingData } from "../lib/api-client.ts";
import { getDefaultZipForState } from "../lib/state-config.ts";
import { calculateUnderwritingFeePercentage } from "../lib/fee-calculator.ts";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";

// Define local interfaces for the component
interface PricingResult {
  baseRate: number;
  finalRate: number;
  adjustments: { name: string; value: number }[];
  alternativeProgram: string;
  totalPayment: number;
  loanType: string;
  amortizationType: string;
  loanTerm: string;
  lockPeriod: number;
  isFromFallback?: boolean;
  monthlyPayment?: {
    monthlyPI: number;
    total: number;
    mi: number;
  };
  borrowerPaid?: number;
  finalClosingCost?: number;
  apr?: number;
  borrowerPaidDetails?: BorrowerPaidDetail[];
  monthlyPaymentMapByRate?: { [key: string]: number };
  disqualified?: boolean;
  disqualifyAdjustments?: string[];
  /**
   * All adjustment details collected from groupAdjustmentProperties
   */
  allAdjustments?: { name: string; type: string; value: number }[];
  /**
   * Price as percent of par (e.g. 99.750 or 101.000), calculated as 100 - discountPoints.
   */
  price: number;
}

interface LoanInput {
  program: string;
  incomeType: string;
  fico: number;
  ltv: number;
  loanAmount: number;
  citizenship: string;
  occupancy: string;
  purpose: string;
  amortizationType: string;
  prepaymentPenalty: string;
  creditEvent: string;
  propertyType: string;
  brokerCompensation: string;
  mortgageHistory: string;
  state: string;
}

const formSchema = z.object({
  program: z.string(),
  mortgageType: z.string(),
  loanType: z.string().optional(),
  incomeType: z.string().optional(),
  citizenship: z.string(),
  occupancy: z.string(),
  rentalTerm: z.string().default("Long_Term_Rental_Property"),
  purpose: z.string(),
  fico: z.number().min(620).max(850),
  ltv: z.number().min(1).max(97),
  loanAmount: z.number().min(1),
  amortizationType: z.string(),
  propertyType: z.string(),
  numberOfUnits: z.string(),
  subFinancing: z.boolean(),
  escrowWaiver: z.boolean(),
  firstTimeHomeBuyer: z.boolean().optional(),
  investorExperience: z.string().default("false"),
  vesting: z.string().optional(),
  mortgageHistory: z.string().optional(),
  creditEvent: z.string(),
  prepaymentPenalty: z.string(),
  lenderFeeBuyOut: z.boolean(),
  underwritingFeePercentage: z.number().default(0.0),
  brokerCompensation: z.string(),
  brokerCompensationValue: z.number().default(1.0),
  dti: z.string(),
  lockPeriod: z.string(),
  state: z.string(),
  zipCode: z.string().optional(),
  interestOnly: z.boolean().optional(),
  fundingFeeFinanced: z.string().optional(),
  guranteeFeeFinanced: z.string().optional(),
  entitlementUsage: z.boolean().optional(),
  veteranType: z.string().optional(),
  achWaiver: z.boolean().optional(),
  dscrValue: z.number().min(0).max(10).optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface Adjustment {
  name: string;
  value: number;
}

interface ProgramOption {
  value: string;
}

interface DropdownOption {
  value: string;
  label: string;
  default?: boolean;
}

// Utility functions
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatDate = (date: Date) => {
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
};

const formatTime = (date: Date) => {
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

interface PricingCalculatorProps {
  initialValues?: Partial<FormValues>;
}

const PricingCalculator: React.FC<PricingCalculatorProps> = ({
  initialValues = {},
}) => {
  const [pricingResults, setPricingResults] = useState<
    { programName: string; results: PricingResult[]; isDisqualified: boolean }[]
  >([]);
  const [currentProgramIndex, setCurrentProgramIndex] = useState(0);
  const [currentRateIndex, setCurrentRateIndex] = useState(0);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [isManualNavigation, setIsManualNavigation] = useState(false);
  const rateDisplayRef = useRef<HTMLHeadingElement>(null);
  const paymentDisplayRef = useRef<HTMLHeadingElement>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isSharePopupOpen, setIsSharePopupOpen] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isAutoCalculating, setIsAutoCalculating] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isResultsStale, setIsResultsStale] = useState(false);
  const [hasCalculatedOnce, setHasCalculatedOnce] = useState(false);
  const [autoCalculationTimeout, setAutoCalculationTimeout] = useState<
    number | null
  >(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const lastCalculatedValues = useRef<FormValues | null>(null);
  const [underwritingFeePercentage, setUnderwritingFeePercentage] = useState<
    number | null
  >(null);
  const [monthlyPayment, setMonthlyPayment] = useState<number>(0);
  const [ficoRange, setFicoRange] = useState({ min: 620, max: 850 });
  const [ltvRange, setLtvRange] = useState({ min: 1, max: 97 });
  const [loanAmountRange, setLoanAmountRange] = useState(
    getLoanAmountRange("Non-QM")
  );

  // Get default values from configuration
  const defaultProgram = "Non-QM";
  const defaultLoanTypes = getLoanTypes(defaultProgram);
  const defaultIncomeTypes = getIncomeTypes(defaultProgram);
  const defaultAmortizationTypes = getAmortizationTypes(defaultProgram);
  const defaultCitizenshipOptions = getCitizenshipOptions(defaultProgram);
  const defaultOccupancyOptions = getOccupancyOptions(defaultProgram);
  const defaultPurposeOptions = getPurposeOptions(defaultProgram);
  const defaultPropertyTypeOptions = getPropertyTypeOptions(defaultProgram);
  const defaultNumberOfUnitsOptions = getNumberOfUnitsOptions(defaultProgram);
  const defaultDtiOptions = getDtiOptions(defaultProgram);
  const defaultLockPeriodOptions = getLockPeriodOptions(defaultProgram);
  const defaultCreditEventOptions = getCreditEventOptions(defaultProgram);
  const defaultPrepaymentPenaltyOptions =
    getPrepaymentPenaltyOptions(defaultProgram);
  const defaultStateOptions = getStateOptions(
    undefined,
    defaultProgram,
    "Primary"
  );
  const defaultMortgageHistoryOptions =
    getMortgageHistoryOptions(defaultProgram);

  // State for dependent dropdowns
  const [availableLoanTypes, setAvailableLoanTypes] = useState<
    DropdownOption[]
  >([]);
  const [availableIncomeTypes, setAvailableIncomeTypes] = useState<
    DropdownOption[]
  >([]);
  const [availableAmortizationTypes, setAvailableAmortizationTypes] = useState<
    DropdownOption[]
  >([]);
  const [availableCitizenshipOptions, setAvailableCitizenshipOptions] =
    useState<DropdownOption[]>([]);
  const [availableOccupancyOptions, setAvailableOccupancyOptions] = useState<
    DropdownOption[]
  >([]);
  const [availablePurposeOptions, setAvailablePurposeOptions] = useState<
    DropdownOption[]
  >([]);
  const [availablePropertyTypeOptions, setAvailablePropertyTypeOptions] =
    useState<DropdownOption[]>([]);
  const [availableNumberOfUnitsOptions, setAvailableNumberOfUnitsOptions] =
    useState<DropdownOption[]>([]);
  const [availableDtiOptions, setAvailableDtiOptions] = useState<
    DropdownOption[]
  >([]);
  const [availableLockPeriodOptions, setAvailableLockPeriodOptions] = useState<
    DropdownOption[]
  >([]);
  const [availableCreditEventOptions, setAvailableCreditEventOptions] =
    useState<DropdownOption[]>([]);
  const [
    availablePrepaymentPenaltyOptions,
    setAvailablePrepaymentPenaltyOptions,
  ] = useState<DropdownOption[]>([]);
  const [availableVestingOptions, setAvailableVestingOptions] = useState<
    DropdownOption[]
  >([]);
  const [availableMortgageHistoryOptions, setAvailableMortgageHistoryOptions] =
    useState<DropdownOption[]>([]);
  const [availableRentalTermOptions, setAvailableRentalTermOptions] = useState<
    DropdownOption[]
  >([
    { value: "Long_Term_Rental_Property", label: "Long Term", default: true },
    {
      value: "Short_Term_Rental_Property",
      label: "Short Term",
      default: false,
    },
  ]);
  const [showLenderFeeBuyOut, setShowLenderFeeBuyOut] = useState(false);
  const [showSubFinancing, setShowSubFinancing] = useState(false);
  const [showEscrowWaiver, setShowEscrowWaiver] = useState(false);
  const [showFirstTimeHomeBuyer, setShowFirstTimeHomeBuyer] = useState(false);
  const [showInvestorExperience, setShowInvestorExperience] = useState(false);
  const [isOccupancyDisabled, setIsOccupancyDisabled] = useState(false);

  const [showInterestOnly, setShowInterestOnly] = useState(false);
  const [showFundingFeeFinanced, setShowFundingFeeFinanced] = useState(false);
  const [showGuranteeFeeFinanced, setShowGuranteeFeeFinanced] = useState(false);
  const [showEntitlementUsage, setShowEntitlementUsage] = useState(false);
  const [showveteranType, setShowveteranType] = useState(false);
  const [brokerCompensationRange, setBrokerCompensationRange] = useState(
    getBrokerCompensationRange(defaultProgram)
  );
  const [showBrokerCompAmount, setShowBrokerCompAmount] = useState(false);

  // Add state to track the rate index for each result
  const [rateIndices, setRateIndices] = useState<number[]>([]);

  // Add state for managing tabs
  const [activeTab, setActiveTab] = useState<"qualified" | "non-qualified">(
    "qualified"
  );

  // Add debug state
  const [debugRequest, setDebugRequest] = useState<any>(null);
  const [debugResponse, setDebugResponse] = useState<any>(null);
  const [debugMapped, setDebugMapped] = useState<any>(null);
  const [showDebug, setShowDebug] = useState(false);
  const [debugInfoEnabled, setDebugInfoEnabled] = useState(false);
  // Add collapse state for each debug item
  const [showReq, setShowReq] = useState(false);
  const [showResp, setShowResp] = useState(false);
  const [showMapped, setShowMapped] = useState(false);
  const [showCalc, setShowCalc] = useState(false);

  // Add state for adjustments popup
  const [isAdjustmentsPopupOpen, setIsAdjustmentsPopupOpen] = useState(false);

  // Add state for full rate stack popup
  const [isRateStackPopupOpen, setIsRateStackPopupOpen] = useState(false);

  const [interestOnlyReadOnly, setInterestOnlyReadOnly] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Helper functions for LPC session storage
  const getLastLPCValue = (): number | null => {
    if (typeof window === "undefined") return null;
    const stored = sessionStorage.getItem("lastLPCValue");
    return stored ? parseFloat(stored) : null;
  };

  const saveLPCValue = (value: number) => {
    if (typeof window === "undefined") return;
    sessionStorage.setItem("lastLPCValue", value.toString());
  };

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: "onSubmit", // Only validate on submit, not on change
    defaultValues: {
      program: "Non-QM",
      mortgageType: "Non-QM",
      loanType:
        defaultLoanTypes.find((t: DropdownOption) => t.default)?.value ||
        defaultLoanTypes[0]?.value,
      incomeType:
        defaultIncomeTypes.find((t: DropdownOption) => t.default)?.value ||
        defaultIncomeTypes[0]?.value,
      amortizationType:
        defaultAmortizationTypes.find((t: DropdownOption) => t.default)
          ?.value || defaultAmortizationTypes[0]?.value,
      citizenship:
        defaultCitizenshipOptions.find((o: DropdownOption) => o.default)
          ?.value || defaultCitizenshipOptions[0]?.value,
      occupancy:
        defaultOccupancyOptions.find((o: DropdownOption) => o.default)?.value ||
        defaultOccupancyOptions[0]?.value,
      rentalTerm: "Long_Term_Rental_Property",
      purpose:
        defaultPurposeOptions.find((o: DropdownOption) => o.default)?.value ||
        defaultPurposeOptions[0]?.value,
      propertyType:
        defaultPropertyTypeOptions.find((o: DropdownOption) => o.default)
          ?.value || defaultPropertyTypeOptions[0]?.value,
      numberOfUnits:
        defaultNumberOfUnitsOptions.find((o: DropdownOption) => o.default)
          ?.value || defaultNumberOfUnitsOptions[0]?.value,
      dti:
        defaultDtiOptions.find((o: DropdownOption) => o.default)?.value ||
        defaultDtiOptions[0]?.value,
      lockPeriod:
        defaultLockPeriodOptions.find((o: DropdownOption) => o.default)
          ?.value || defaultLockPeriodOptions[0]?.value,
      creditEvent:
        defaultCreditEventOptions.find((o: DropdownOption) => o.default)
          ?.value || defaultCreditEventOptions[0]?.value,
      mortgageHistory:
        defaultMortgageHistoryOptions.find((o: DropdownOption) => o.default)
          ?.value || defaultMortgageHistoryOptions[0]?.value,
      prepaymentPenalty:
        defaultPrepaymentPenaltyOptions.find((o: DropdownOption) => o.default)
          ?.value || defaultPrepaymentPenaltyOptions[0]?.value,
      state:
        defaultStateOptions.find((o: DropdownOption) => o.default)?.value ||
        defaultStateOptions[0]?.value,
      fico: 740,
      ltv: 80,
      loanAmount: 500000,
      subFinancing: false,
      lenderFeeBuyOut: false,
      underwritingFeePercentage: 0.0,
      brokerCompensation: "BPC",
      brokerCompensationValue: 0.0,
      investorExperience: "false",
      escrowWaiver: false,
      vesting: "Individual",
      interestOnly: false,
      fundingFeeFinanced: "NO",
      guranteeFeeFinanced: "NO",
      entitlementUsage: false,
      veteranType: "",
      achWaiver: false,
      dscrValue: undefined,
      ...initialValues,
    },
  });

  // Watch program changes to update all available options
  const watchedProgramMortgageType = form.watch("program");
  const watchedIncomeType = form.watch("incomeType");
  const watchedValues = form.watch();
  // Add this above the useEffect to watch occupancy
  const watchedOccupancy = form.watch("occupancy");
  const watchedPropertyType = form.watch("propertyType");
  const watchedNumberOfUnits = form.watch("numberOfUnits");

  // Parse program and mortgageType from the combined value
  const programParts = (watchedProgramMortgageType || "").split(":");
  const watchedProgram = programParts[0];
  const watchedMortgageType =
    programParts.length > 1 ? programParts[1] : programParts[0];

  // DEBUG: Log program selection details
  console.log("🔍 PROGRAM SELECTION DEBUG:", {
    watchedProgramMortgageType,
    programParts,
    watchedProgram,
    watchedMortgageType,
    "programConfig keys": Object.keys(programConfig.programConfigs),
    "has Second Lien config": !!programConfig.programConfigs["Second Lien"],
    "Second Lien mortgageTypes":
      programConfig.programConfigs["Second Lien"]?.mortgageTypes,
  });

  // Update mortgageType based on program config
  useEffect(() => {
    if (!watchedProgram) return;

    const currentProgramConfig = programConfig.programConfigs[watchedProgram];
    if (
      currentProgramConfig?.mortgageTypes &&
      currentProgramConfig.mortgageTypes.length > 0
    ) {
      // If program has mortgageTypes config, use the selected mortgageType
      form.setValue("mortgageType", watchedMortgageType);
    } else {
      // If program doesn't have mortgageTypes config, use the second part of the program string
      form.setValue("mortgageType", watchedMortgageType || watchedProgram);
    }
  }, [watchedProgram, watchedMortgageType, form]);

  // Update DSCR-related logic based on income type
  useEffect(() => {
    const isDSCR = watchedIncomeType?.includes("DSCR") || false;

    // Disable broker compensation for DSCR loans
    if (isDSCR) {
      form.setValue("brokerCompensation", "BPC");
      form.setValue("brokerCompensationValue", 0);
      // Set occupancy to investment for DSCR loans and disable the field
      form.setValue("occupancy", "Investment", { shouldValidate: false });
      setIsOccupancyDisabled(true);
    } else {
      // Re-enable occupancy field when not DSCR
      setIsOccupancyDisabled(false);
    }

    // SuperNONI asset depletion: lock DSCR at 1.15 (1.15+ bucket)
    if (watchedIncomeType === "DSCR-AssetUtilization") {
      form.setValue("dscrValue", 1.15);
      // SuperNONI: FTHB ineligible — clear if switching from NONI/DSCR
      form.setValue("firstTimeHomeBuyer", false);
    } else {
      form.setValue("dscrValue", 1.25);
    }
  }, [watchedIncomeType, watchedOccupancy, form]);

  // Add handleFieldChange function
  // Debounced auto-calculation function
  const triggerAutoCalculation = useCallback(() => {
    // Only auto-calculate if user has manually calculated at least once
    if (!hasCalculatedOnce) {
      console.log(
        "🚫 Skipping auto-calculation - user hasn't manually calculated yet"
      );
      return;
    }

    // Clear any existing timeout
    if (autoCalculationTimeout) {
      clearTimeout(autoCalculationTimeout);
    }

    // Set a new timeout for 500ms delay
    const timeout = setTimeout(async () => {
      console.log("⏰ Auto-calculation timeout triggered");

      // Get form values and check for critical validation issues
      const formValues = form.getValues();
      console.log("🔍 Current form values for auto-calculation:", formValues);

      const criticalFields = [
        "program",
        "fico",
        "ltv",
        "loanAmount",
        "amortizationType",
      ];
      const hasCriticalValues = criticalFields.every((field) => {
        const value = formValues[field as keyof FormValues];
        return value !== undefined && value !== null && value !== "";
      });

      if (!hasCriticalValues) {
        console.log("❌ Missing critical values, skipping auto-calculation");
        console.log(
          "🔍 Critical fields status:",
          criticalFields.map((field) => ({
            field,
            value: formValues[field as keyof FormValues],
            hasValue:
              formValues[field as keyof FormValues] !== undefined &&
              formValues[field as keyof FormValues] !== null &&
              formValues[field as keyof FormValues] !== "",
          }))
        );
        return; // Don't auto-calculate if critical fields are missing
      }

      console.log(
        "✅ Critical fields have values, proceeding with calculation"
      );

      // Try to validate the form, but don't fail completely if validation fails
      try {
        const validationResult = await form.trigger();
        console.log("🔍 Form validation result:", validationResult);
      } catch (error) {
        console.log(
          "⚠️ Form validation had issues, but proceeding with calculation:",
          error
        );
      }

      // Cancel any existing request
      if (abortControllerRef.current) {
        try {
          abortControllerRef.current.abort();
        } catch (error) {
          // Ignore abort errors - this can happen if the controller was already aborted
          console.log("Abort controller already aborted, continuing...");
        }
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      setIsAutoCalculating(true);
      setApiError(null);

      try {
        console.log("✅ Starting API call with form values");
        console.log("📤 Sending form values:", formValues);

        // Reset debug info for auto-calculation
        if (debugInfoEnabled) {
          console.log("🔍 Debug info enabled - resetting debug states");
          setDebugRequest(null);
          setDebugResponse(null);
          setDebugMapped(null);
        } else {
          console.log("🔍 Debug info NOT enabled");
        }

        const results = await fetchPricingData(
          formValues,
          abortControllerRef.current?.signal,
          debugInfoEnabled
            ? (reqObj) => {
                console.log("🔍 Setting debug request:", reqObj);
                setDebugRequest(reqObj);
              }
            : undefined, // callback to get request
          debugInfoEnabled
            ? (rawResp) => {
                console.log("🔍 Setting debug response:", rawResp);
                setDebugResponse(rawResp);
              }
            : undefined // callback to get raw response
        );
        console.log("📥 Received results:", results);

        // Filter out rates with 'No data available for this rate' and all-zero results
        let filteredResults = results
          .map((entry: any) => ({
            ...entry,
            results: entry.results.filter(
              (r: any) =>
                r.alternativeProgram !== "No data available for this rate" &&
                !(
                  (r.finalRate === 0 || r.finalRate === undefined) &&
                  (r.totalPayment === 0 || r.totalPayment === undefined) &&
                  (r.price === 0 || r.price === undefined)
                )
            ),
          }))
          .filter((entry: any) => entry.results.length > 0);

        console.log("🎉 Auto-calculation successful, setting results");
        setPricingResults(filteredResults);
        setCurrentTime(new Date());
        setIsResultsStale(false);
        lastCalculatedValues.current = formValues;

        // Set optimal rate index for the current program (same logic as manual calculation)
        const currentProgram = filteredResults[currentProgramIndex];
        if (currentProgram?.results) {
          setCurrentRateIndex(findOptimalRateIndex(currentProgram.results));
        } else {
          setCurrentRateIndex(0);
        }

        // Set debug mapped results for auto-calculation
        if (debugInfoEnabled) {
          const mappedData = {
            filteredResults,
            // Include error information if present
            ...(results && (results as any).debugAdjustments?.error
              ? { error: (results as any).debugAdjustments.error }
              : {}),
          };
          console.log("🔍 Setting debug mapped results:", mappedData);
          setDebugMapped(mappedData);
        }
      } catch (error: any) {
        if (error.name === "AbortError") {
          // Request was cancelled, ignore
          return;
        }
        console.error("Auto-calculation error:", error);
        setApiError(error.message || "An error occurred during calculation");

        // Add error to debug info for auto-calculation
        if (debugInfoEnabled) {
          setDebugMapped((prev) => ({
            ...prev,
            error: {
              message: error.message || "An error occurred during calculation",
              stack: error instanceof Error ? error.stack : undefined,
              timestamp: new Date().toISOString(),
            },
          }));
        }
      } finally {
        setIsAutoCalculating(false);
      }
    }, 500);

    setAutoCalculationTimeout(timeout);
  }, [form, debugInfoEnabled, hasCalculatedOnce]);

  const handleFieldChange = (fieldName: keyof FormValues, value: any) => {
    console.log(`🔄 Field change: ${fieldName} = ${value}`);
    form.setValue(fieldName, value);

    // Trigger auto-calculation for most fields, but exclude some that don't affect pricing
    const fieldsToExclude = ["zipCode"]; // Add fields that shouldn't trigger auto-calculation
    if (!fieldsToExclude.includes(fieldName)) {
      console.log(`🔄 Auto-calculation triggered for field: ${fieldName}`);
      // Mark results as stale when a field changes, but don't clear them
      if (pricingResults.length > 0) {
        setIsResultsStale(true);
      }
      triggerAutoCalculation();
    }
  };

  // Handler for text/number inputs that only triggers auto-calculation on blur
  const handleFieldChangeNoAutoCalc = (
    fieldName: keyof FormValues,
    value: any
  ) => {
    console.log(`🔄 Field change (no auto-calc): ${fieldName} = ${value}`);
    form.setValue(fieldName, value);
  };

  // Handler for onBlur events that triggers auto-calculation
  const handleFieldBlur = (fieldName: keyof FormValues) => {
    console.log(`🔄 Field blur - triggering auto-calculation: ${fieldName}`);
    // Mark results as stale when a field loses focus, but don't clear them
    if (pricingResults.length > 0) {
      setIsResultsStale(true);
    }
    triggerAutoCalculation();
  };

  // Enhanced field change handler that triggers recalculation
  const handleFieldChangeWithRecalculate = (
    fieldName: keyof FormValues,
    value: any
  ) => {
    console.log(`🔄 Enhanced field change: ${fieldName} = ${value}`);
    handleFieldChange(fieldName, value);
  };

  // Only reset state value if the current selection is not available in the new options
  useEffect(() => {
    console.log(
      "Checking state validity for watchedIncomeType:",
      watchedIncomeType,
      "watchedProgram:",
      watchedProgram
    );

    const availableStates = getStateOptions(
      watchedIncomeType,
      watchedProgram,
      watchedOccupancy
    );

    console.log(
      "Available states:",
      availableStates.map((o: DropdownOption) => o.value)
    );

    const currentState = form.getValues("state");
    const isCurrentStateValid = availableStates.some(
      (option: DropdownOption) => option.value === currentState
    );

    // Only reset if current state is not valid for the new configuration
    if (!isCurrentStateValid) {
      console.log(
        `Current state "${currentState}" is not valid for new configuration, resetting to default`
      );
      form.setValue(
        "state",
        availableStates.find((o: DropdownOption) => o.default)?.value ||
          availableStates[0]?.value
      );
    } else {
      console.log(
        `Current state "${currentState}" is still valid, preserving user selection`
      );
    }
  }, [watchedIncomeType, watchedProgram, watchedOccupancy, form]);

  useEffect(() => {
    // Update all available options based on the selected program and mortgageType
    if (!watchedProgram) return;
    // For each dropdown, if the current value is not in the new options, reset it

    // Get the current program config
    const currentProgramConfig = programConfig.programConfigs[watchedProgram];

    // DEBUG: Log program config lookup
    console.log("🔍 PROGRAM CONFIG DEBUG:", {
      watchedProgram,
      watchedMortgageType,
      "currentProgramConfig exists": !!currentProgramConfig,
      "currentProgramConfig keys": currentProgramConfig
        ? Object.keys(currentProgramConfig)
        : null,
      "mortgageTypes in config": currentProgramConfig?.mortgageTypes,
      "amortization in config": currentProgramConfig?.amortization,
      "incomeTypes in config": currentProgramConfig?.incomeTypes,
    });

    // Update boolean field visibility
    // Hide Lender Fee Buyout for DSCR and DSCR-AssetUtilization income types
    const shouldHideLenderFeeBuyOut =
      watchedIncomeType === "DSCR" ||
      watchedIncomeType === "DSCR-AssetUtilization";
    setShowLenderFeeBuyOut(
      (currentProgramConfig?.lenderFeeBuyOut ?? false) &&
        !shouldHideLenderFeeBuyOut
    );
    setShowSubFinancing(currentProgramConfig?.subFinancing ?? false);
    setShowEscrowWaiver(currentProgramConfig?.escrowWaiver ?? false);
    setShowFirstTimeHomeBuyer(
      currentProgramConfig?.firstTimeHomeBuyer ?? false
    );
    setShowInvestorExperience(currentProgramConfig?.firstTimeInvestor ?? false);

    // Show funding fee dropdowns based on mortgage type
    const mortgageType = form.getValues("mortgageType");
    setShowFundingFeeFinanced(mortgageType === "FHA");
    setShowGuranteeFeeFinanced(
      mortgageType === "VA" || mortgageType === "USDA"
    );
    setShowEntitlementUsage(mortgageType === "VA");
    setShowveteranType(mortgageType === "VA");

    // Reset boolean fields if they're not available for the current program
    if (!currentProgramConfig?.lenderFeeBuyOut) {
      form.setValue("lenderFeeBuyOut", false);
    }
    if (!currentProgramConfig?.subFinancing) {
      form.setValue("subFinancing", false);
    }
    if (!currentProgramConfig?.escrowWaiver) {
      form.setValue("escrowWaiver", false);
    }
    if (!currentProgramConfig?.firstTimeHomeBuyer) {
      form.setValue("firstTimeHomeBuyer", false);
    }
    if (!currentProgramConfig?.firstTimeInvestor) {
      form.setValue("investorExperience", "false");
    }

    // Loan Types
    const newLoanTypes = getLoanTypes(watchedMortgageType);
    console.log("🔍 LOAN TYPES DEBUG:", {
      "called with": watchedMortgageType,
      "should be called with": watchedMortgageType,
      result: newLoanTypes,
    });
    setAvailableLoanTypes(newLoanTypes);
    const currentLoanType = form.getValues("loanType");
    const loanTypeValues = newLoanTypes.map((opt: DropdownOption) => opt.value);
    if (!loanTypeValues.includes(currentLoanType || "")) {
      form.setValue(
        "loanType",
        newLoanTypes.find((t: DropdownOption) => t.default)?.value ||
          newLoanTypes[0]?.value
      );
    }

    // Income Type
    const newIncomeTypes = getIncomeTypes(watchedMortgageType);
    console.log("🔍 INCOME TYPES DEBUG:", {
      "called with": watchedMortgageType,
      "should be called with": watchedMortgageType,
      result: newIncomeTypes,
    });
    setAvailableIncomeTypes(newIncomeTypes);
    const currentIncomeType = form.getValues("incomeType");
    const incomeTypeValues = newIncomeTypes.map(
      (opt: DropdownOption) => opt.value
    );
    if (!incomeTypeValues.includes(currentIncomeType || "")) {
      form.setValue(
        "incomeType",
        newIncomeTypes.find((t: DropdownOption) => t.default)?.value ||
          newIncomeTypes[0]?.value
      );
    }

    // Amortization Types
    const newAmortizationTypes = getAmortizationTypes(watchedMortgageType);
    console.log("🔍 AMORTIZATION TYPES DEBUG:", {
      "called with": watchedMortgageType,
      "should be called with": watchedMortgageType,
      result: newAmortizationTypes,
    });
    setAvailableAmortizationTypes(newAmortizationTypes);
    const currentAmortizationType = form.getValues("amortizationType");
    const amortizationTypeValues = newAmortizationTypes.map(
      (opt: DropdownOption) => opt.value
    );
    if (!amortizationTypeValues.includes(currentAmortizationType)) {
      form.setValue(
        "amortizationType",
        newAmortizationTypes.find((t: DropdownOption) => t.default)?.value ||
          newAmortizationTypes[0]?.value
      );
    }

    // Citizenship
    const newCitizenshipOptions = getCitizenshipOptions(watchedMortgageType);

    // Filter out ITIN option for specific income types
    const incomeTypesWithoutITIN = [
      "DSCR",
      "DSCR-AssetUtilization",
      "24MoCPAPreparedPL", // 2yr P&L
      "12MoCPAPreparedPL", // 1yr P&L
      "24MoCPAPreparedPLwBKStmt", // 2yr P&L with 3 month bank statement
      "12MoCPAPreparedPLwBKStmt", // 1yr P&L with 3 month bank statement
      "Asset Utilization",
      "1099 - 12M", // 1099
    ];

    const filteredCitizenshipOptions = incomeTypesWithoutITIN.includes(
      currentIncomeType || ""
    )
      ? newCitizenshipOptions.filter((option) => option.value !== "ITIN")
      : newCitizenshipOptions;

    setAvailableCitizenshipOptions(filteredCitizenshipOptions);
    const currentCitizenship = form.getValues("citizenship");
    const citizenshipValues = filteredCitizenshipOptions.map(
      (opt: DropdownOption) => opt.value
    );
    if (!citizenshipValues.includes(currentCitizenship)) {
      form.setValue(
        "citizenship",
        filteredCitizenshipOptions.find((t: DropdownOption) => t.default)
          ?.value || filteredCitizenshipOptions[0]?.value
      );
    }

    // Occupancy
    const newOccupancyOptions = getOccupancyOptions(watchedMortgageType);
    setAvailableOccupancyOptions(newOccupancyOptions);
    const currentOccupancy = form.getValues("occupancy");
    const occupancyValues = newOccupancyOptions.map(
      (opt: DropdownOption) => opt.value
    );
    if (!occupancyValues.includes(currentOccupancy)) {
      form.setValue(
        "occupancy",
        newOccupancyOptions.find((t: DropdownOption) => t.default)?.value ||
          newOccupancyOptions[0]?.value
      );
    }

    // Purpose
    const newPurposeOptions = getPurposeOptions(watchedMortgageType);
    setAvailablePurposeOptions(newPurposeOptions);
    const currentPurpose = form.getValues("purpose");
    const purposeValues = newPurposeOptions.map(
      (opt: DropdownOption) => opt.value
    );
    if (!purposeValues.includes(currentPurpose)) {
      form.setValue(
        "purpose",
        newPurposeOptions.find((t: DropdownOption) => t.default)?.value ||
          newPurposeOptions[0]?.value
      );
    }

    // Property Type
    let newPropertyTypeOptions = getPropertyTypeOptions(watchedMortgageType);

    // Filter property types based on income type
    // Non-Warrantable Condo should only be available for DSCR income types
    const incomeTypeForPropertyFilter = form.getValues("incomeType");
    const isDSCRIncomeType = incomeTypeForPropertyFilter?.includes("DSCR");

    if (!isDSCRIncomeType) {
      newPropertyTypeOptions = newPropertyTypeOptions.filter(
        (opt: DropdownOption) => opt.value !== "Non-Warrantable Condo"
      );
    }

    setAvailablePropertyTypeOptions(newPropertyTypeOptions);
    const currentPropertyType = form.getValues("propertyType");
    const propertyTypeValues = newPropertyTypeOptions.map(
      (opt: DropdownOption) => opt.value
    );
    if (!propertyTypeValues.includes(currentPropertyType)) {
      form.setValue(
        "propertyType",
        newPropertyTypeOptions.find((t: DropdownOption) => t.default)?.value ||
          newPropertyTypeOptions[0]?.value
      );
    }

    // Number of Units
    const newNumberOfUnitsOptions =
      getNumberOfUnitsOptions(watchedMortgageType);
    setAvailableNumberOfUnitsOptions(newNumberOfUnitsOptions);
    const currentNumberOfUnits = form.getValues("numberOfUnits");
    const numberOfUnitsValues = newNumberOfUnitsOptions.map(
      (opt: DropdownOption) => opt.value
    );
    if (!numberOfUnitsValues.includes(currentNumberOfUnits)) {
      form.setValue(
        "numberOfUnits",
        newNumberOfUnitsOptions.find((t: DropdownOption) => t.default)?.value ||
          newNumberOfUnitsOptions[0]?.value
      );
    }

    // DTI
    const newDtiOptions = getDtiOptions(watchedMortgageType);
    setAvailableDtiOptions(newDtiOptions);
    const currentDti = form.getValues("dti");
    const dtiValues = newDtiOptions.map((opt: DropdownOption) => opt.value);
    if (!dtiValues.includes(currentDti)) {
      form.setValue(
        "dti",
        newDtiOptions.find((t: DropdownOption) => t.default)?.value ||
          newDtiOptions[0]?.value
      );
    }

    // Lock Period
    const newLockPeriodOptions = getLockPeriodOptions(watchedMortgageType);
    setAvailableLockPeriodOptions(newLockPeriodOptions);
    const currentLockPeriod = form.getValues("lockPeriod");
    const lockPeriodValues = newLockPeriodOptions.map(
      (opt: DropdownOption) => opt.value
    );
    if (!lockPeriodValues.includes(currentLockPeriod)) {
      form.setValue(
        "lockPeriod",
        newLockPeriodOptions.find((t: DropdownOption) => t.default)?.value ||
          newLockPeriodOptions[0]?.value
      );
    }

    // Credit Event
    const newCreditEventOptions = getCreditEventOptions(watchedMortgageType);
    setAvailableCreditEventOptions(newCreditEventOptions);
    const currentCreditEvent = form.getValues("creditEvent");
    const creditEventValues = newCreditEventOptions.map(
      (opt: DropdownOption) => opt.value
    );
    if (!creditEventValues.includes(currentCreditEvent)) {
      form.setValue(
        "creditEvent",
        newCreditEventOptions.find((t: DropdownOption) => t.default)?.value ||
          newCreditEventOptions[0]?.value
      );
    }

    // Prepayment Penalty
    const newPrepaymentPenaltyOptions = getPrepaymentPenaltyOptions(
      watchedMortgageType,
      watchedOccupancy,
      watchedIncomeType || "",
      watchedPropertyType,
      watchedNumberOfUnits
    );

    setAvailablePrepaymentPenaltyOptions(newPrepaymentPenaltyOptions);
    const currentPrepaymentPenalty = form.getValues("prepaymentPenalty");
    const prepaymentPenaltyValues = newPrepaymentPenaltyOptions.map(
      (opt: DropdownOption) => opt.value
    );

    if (
      !prepaymentPenaltyValues.includes(currentPrepaymentPenalty) &&
      newPrepaymentPenaltyOptions.length > 0
    ) {
      const defaultOption = newPrepaymentPenaltyOptions.find(
        (t: DropdownOption) => t.default
      );
      const firstOption = newPrepaymentPenaltyOptions[0];
      const newValue = defaultOption?.value || firstOption?.value;

      form.setValue("prepaymentPenalty", newValue, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    setBrokerCompensationRange(getBrokerCompensationRange(watchedMortgageType));
    setLoanAmountRange(getLoanAmountRange(watchedMortgageType));
    // Add FICO and LTV range updates
    const programConfigs = require("../lib/program-config.ts").default
      .programConfigs;
    const config = programConfigs[watchedProgram];
    if (config) {
      setFicoRange(config.fico);
      setLtvRange(config.ltv);
    }

    // Vesting
    const newVestingOptions = getVestingOptions(watchedMortgageType);
    setAvailableVestingOptions(newVestingOptions);
    const newMortgageHistoryOptions =
      getMortgageHistoryOptions(watchedMortgageType);
    setAvailableMortgageHistoryOptions(newMortgageHistoryOptions);

    // Reset mortgageHistory to undefined when program changes
    form.setValue("mortgageHistory", undefined);

    const currentVesting = form.getValues("vesting");
    const vestingValues = newVestingOptions.map(
      (opt: DropdownOption) => opt.value
    );
    if (!vestingValues.includes(currentVesting || "")) {
      form.setValue(
        "vesting",
        newVestingOptions.find((t: DropdownOption) => t.default)?.value ||
          newVestingOptions[0]?.value
      );
    }

    // Set mortgageHistory to default value after reset
    const currentMortgageHistory = form.getValues("mortgageHistory");
    const mortgageHistoryValues = newMortgageHistoryOptions.map(
      (opt: DropdownOption) => opt.value
    );
    if (
      !mortgageHistoryValues.includes(currentMortgageHistory || "") &&
      newMortgageHistoryOptions.length > 0
    ) {
      form.setValue(
        "mortgageHistory",
        newMortgageHistoryOptions.find((t: DropdownOption) => t.default)
          ?.value || newMortgageHistoryOptions[0]?.value
      );
    }

    setShowInterestOnly(currentProgramConfig?.interestOnly ?? false);
    if (!currentProgramConfig?.interestOnly) {
      form.setValue("interestOnly", false);
    }
  }, [
    watchedProgram,
    watchedMortgageType,
    watchedIncomeType,
    watchedOccupancy,
    watchedPropertyType,
    watchedNumberOfUnits,
    form,
  ]);

  // Update amortization types when program or income type changes
  useEffect(() => {
    const loanType = form.getValues("loanType");
    if (loanType === "HomeReady" || loanType === "HomePossible") {
      const thirtyYearFixed = {
        value: "30-fixed",
        label: "30yr Fixed",
        default: true,
      };
      setAvailableAmortizationTypes([thirtyYearFixed]);
    } else {
      const amortizationTypes = getAmortizationTypes(watchedMortgageType);
      setAvailableAmortizationTypes(amortizationTypes);
    }
  }, [watchedProgram, watchedIncomeType, form.watch("loanType")]);

  // Ensure amortizationType value is always valid and preselected
  useEffect(() => {
    const loanType = form.getValues("loanType");
    if (loanType === "HomeReady" || loanType === "HomePossible") {
      if (form.getValues("amortizationType") !== "30-fixed") {
        form.setValue("amortizationType", "30-fixed", {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    } else {
      const currentValue = form.getValues("amortizationType");
      if (
        !availableAmortizationTypes.some((type) => type.value === currentValue)
      ) {
        const defaultType =
          availableAmortizationTypes.find((type) => type.default) ||
          availableAmortizationTypes[0];
        if (defaultType) {
          form.setValue("amortizationType", defaultType.value, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
      }
    }
  }, [availableAmortizationTypes, form.watch("loanType")]);

  // Add this helper to flatten ALL results from each pricingResults entry
  const flatResults = pricingResults
    .flatMap((entry) => entry.results)
    .filter(Boolean);

  // Separate qualified and non-qualified results
  const qualifiedResults = flatResults.filter(
    (result) =>
      !result.disqualifyAdjustments || result.disqualifyAdjustments.length === 0
  );

  const nonQualifiedResults = flatResults.filter(
    (result) =>
      result.disqualifyAdjustments && result.disqualifyAdjustments.length > 0
  );

  // Add this helper to get programs based on active tab
  const getProgramsForTab = () => {
    return pricingResults.filter((program) =>
      activeTab === "qualified"
        ? !program.isDisqualified
        : program.isDisqualified
    );
  };

  // Get current programs for the active tab
  const currentPrograms = getProgramsForTab();

  // Get current program
  const currentProgram = currentPrograms[currentProgramIndex] || null;

  // Debug program selection
  console.log("🔍 Program Selection Debug:");
  console.log("  currentProgramIndex:", currentProgramIndex);
  console.log("  currentPrograms.length:", currentPrograms.length);
  console.log("  currentProgram:", currentProgram);
  console.log(
    "  currentProgram?.results?.length:",
    currentProgram?.results?.length
  );

  // Get current results for the selected program - simplified without useMemo
  const currentResults = currentProgram?.results || [];

  // Update navigation logic to work with program and rate indices
  const goToNextRate = () => {
    if (
      currentResults.length > 0 &&
      currentRateIndex < currentResults.length - 1
    ) {
      console.log("🔍 goToNextRate - before update:", currentRateIndex);
      const newIndex = currentRateIndex + 1;
      console.log(
        "🔍 goToNextRate - updating from",
        currentRateIndex,
        "to",
        newIndex
      );
      setIsManualNavigation(true);
      setCurrentRateIndex(newIndex);
      setForceUpdate((prev) => prev + 1); // Force re-render

      // Force immediate re-render with setTimeout
      setTimeout(() => {
        setForceUpdate((prev) => prev + 1);
        setIsManualNavigation(false); // Reset flag after navigation
      }, 0);
    }
  };

  const goToPreviousRate = () => {
    if (currentRateIndex > 0) {
      console.log("🔍 goToPreviousRate - before update:", currentRateIndex);
      const newIndex = currentRateIndex - 1;
      console.log(
        "🔍 goToPreviousRate - updating from",
        currentRateIndex,
        "to",
        newIndex
      );
      setIsManualNavigation(true);
      setCurrentRateIndex(newIndex);
      setForceUpdate((prev) => prev + 1); // Force re-render

      // Force immediate re-render with setTimeout
      setTimeout(() => {
        setForceUpdate((prev) => prev + 1);
        setIsManualNavigation(false); // Reset flag after navigation
      }, 0);
    }
  };

  const goToNextProgram = () => {
    if (
      currentPrograms.length > 0 &&
      currentProgramIndex < currentPrograms.length - 1
    ) {
      const newProgramIndex = currentProgramIndex + 1;
      setCurrentProgramIndex(newProgramIndex);
      // Set optimal rate for the new program
      const newProgram = currentPrograms[newProgramIndex];
      if (newProgram?.results) {
        setCurrentRateIndex(findOptimalRateIndex(newProgram.results));
      } else {
        setCurrentRateIndex(0);
      }
    }
  };

  const goToPreviousProgram = () => {
    if (currentProgramIndex > 0) {
      const newProgramIndex = currentProgramIndex - 1;
      setCurrentProgramIndex(newProgramIndex);
      // Set optimal rate for the new program
      const newProgram = currentPrograms[newProgramIndex];
      if (newProgram?.results) {
        setCurrentRateIndex(findOptimalRateIndex(newProgram.results));
      } else {
        setCurrentRateIndex(0);
      }
    }
  };

  // Reset program index and rate index when switching tabs
  useEffect(() => {
    setCurrentProgramIndex(0);
    setCurrentRateIndex(0); // Force reset to 0 when switching tabs
  }, [activeTab]);

  // Reset rate index when program changes or when switching tabs
  // BUT NOT when we're manually navigating with arrows
  useEffect(() => {
    // Only reset if we're not in the middle of manual navigation
    if (forceUpdate === 0) {
      const currentProgram = currentPrograms[currentProgramIndex];
      if (currentProgram?.results) {
        const optimalIndex = findOptimalRateIndex(currentProgram.results);
        // Ensure the calculated index is within bounds
        const safeIndex = Math.min(
          optimalIndex,
          currentProgram.results.length - 1
        );
        console.log("🔍 Auto-resetting rate index to optimal:", safeIndex);
        setCurrentRateIndex(safeIndex);
      } else {
        console.log("🔍 Auto-resetting rate index to 0");
        setCurrentRateIndex(0);
      }
    } else {
      console.log(
        "🔍 Skipping auto-reset due to manual navigation (forceUpdate:",
        forceUpdate,
        ")"
      );
    }
  }, [currentProgramIndex, currentPrograms, activeTab, forceUpdate]);

  // Reset indices when results change (only for boundary conditions)
  useEffect(() => {
    if (
      currentProgramIndex >= currentPrograms.length &&
      currentPrograms.length > 0
    ) {
      setCurrentProgramIndex(0);
    }
    if (
      currentRateIndex >= currentResults.length &&
      currentResults.length > 0
    ) {
      setCurrentRateIndex(0);
    }
  }, [
    currentPrograms.length,
    currentResults.length,
    currentProgramIndex,
    currentRateIndex,
  ]);

  // Helper function to find optimal rate index based on price criteria
  const findOptimalRateIndex = (results: typeof currentResults): number => {
    if (!results || results.length === 0) return 0;

    // Find rates with price >= 100 (closest to 100)
    const ratesAbove100 = results
      .map((result, index) => ({ ...result, originalIndex: index }))
      .filter((result) => result.price >= 100);

    if (ratesAbove100.length > 0) {
      // Find the rate closest to 100 among those >= 100
      const closest = ratesAbove100.reduce((prev, current) =>
        Math.abs(current.price - 100) < Math.abs(prev.price - 100)
          ? current
          : prev
      );
      return closest.originalIndex;
    }

    // If no rates >= 100, find the rate closest to 100 among those < 100
    const ratesBelow100 = results
      .map((result, index) => ({ ...result, originalIndex: index }))
      .filter((result) => result.price < 100);

    if (ratesBelow100.length > 0) {
      const closest = ratesBelow100.reduce((prev, current) =>
        Math.abs(current.price - 100) < Math.abs(prev.price - 100)
          ? current
          : prev
      );
      return closest.originalIndex;
    }

    // Fallback to first rate if somehow no rates found
    return 0;
  };

  // Helper to get the current product - simplified without useMemo
  const currentProduct = (() => {
    const product =
      currentResults &&
      currentResults.length > 0 &&
      currentRateIndex >= 0 &&
      currentRateIndex < currentResults.length
        ? currentResults[currentRateIndex]
        : null;

    console.log("🔍 currentProduct calculation (direct):");
    console.log("  currentRateIndex:", currentRateIndex);
    console.log("  currentResults.length:", currentResults?.length);
    console.log("  selected product:", product);
    console.log("  product finalRate:", product?.finalRate);
    console.log("  forceUpdate:", forceUpdate);

    return product;
  })();

  // Update effect for monthly payment to use totalPayment
  useEffect(() => {
    console.log("🔍 monthlyPayment useEffect triggered");
    console.log("  currentProduct:", currentProduct);
    console.log("  currentProduct.totalPayment:", currentProduct?.totalPayment);
    if (currentProduct) {
      console.log("  Setting monthlyPayment to:", currentProduct.totalPayment);
      setMonthlyPayment(currentProduct.totalPayment);
    }
  }, [currentProduct]);

  // Direct DOM update effect to force UI changes
  useEffect(() => {
    if (currentProduct && rateDisplayRef.current && paymentDisplayRef.current) {
      console.log("🔍 Direct DOM update - updating elements");
      console.log("  Rate:", currentProduct.finalRate?.toFixed(3));
      console.log("  Payment:", currentProduct.totalPayment);
      console.log("  forceUpdate:", forceUpdate);

      const rateText = `${currentProduct.finalRate?.toFixed(3) || "N/A"}%`;
      const paymentText = formatCurrency(currentProduct.totalPayment);

      console.log("  Setting rate text to:", rateText);
      console.log("  Setting payment text to:", paymentText);

      rateDisplayRef.current.textContent = rateText;
      paymentDisplayRef.current.textContent = paymentText;

      console.log("  DOM elements updated successfully");
    }
  }, [currentProduct, forceUpdate]);

  // Use currentProduct for display - simplified
  const displayProduct = currentProduct;

  // Debug log for current product
  useEffect(() => {
    console.log("=== DEBUG INFO ===");
    console.log("Active Tab:", activeTab);
    console.log("Current Program Index:", currentProgramIndex);
    console.log("Current Rate Index:", currentRateIndex);
    console.log("Current Programs:", currentPrograms);
    console.log("Current Program:", currentProgram);
    console.log("Current Results:", currentResults);
    console.log("Current Product:", currentProduct);
    console.log("Total Results Length:", currentResults.length);
    console.log("Pricing Results:", pricingResults);
    console.log("==================");
  }, [
    currentProduct,
    currentRateIndex,
    currentResults.length,
    activeTab,
    currentProgramIndex,
    currentPrograms,
    currentProgram,
    pricingResults,
  ]);

  // Add effect to track form changes
  useEffect(() => {
    if (!lastCalculatedValues.current || pricingResults.length === 0) return;

    const currentValues = form.getValues();
    const hasChanged = Object.keys(currentValues).some((key) => {
      const currentValue = currentValues[key as keyof FormValues];
      const lastValue = lastCalculatedValues.current?.[key as keyof FormValues];
      return currentValue !== lastValue;
    });

    if (hasChanged) {
      setIsResultsStale(true);
      // Don't clear results during auto-calculation - just mark them as stale
      // setPricingResults([]);
      // setCurrentRateIndex(0);
    }
  }, [form.watch()]);

  // Update the form's zip code whenever state changes
  useEffect(() => {
    const stateValue = form.getValues("state");
    const defaultZip = getDefaultZipForState(stateValue);
    form.setValue("zipCode", defaultZip);
  }, [form.watch("state")]);

  // Check for debugInfo query parameter on component mount
  useEffect(() => {
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search);
      const debugInfo = urlParams.get("debugInfo");
      const isEnabled = debugInfo === "true";
      console.log(
        "🔍 Debug info check - URL param:",
        debugInfo,
        "Enabled:",
        isEnabled
      );
      setDebugInfoEnabled(isEnabled);
    }
  }, []);

  // Initial setup on component mount - no longer auto-calculating
  useEffect(() => {
    console.log(
      "🚀 [DEBUG] Component mounted - waiting for user to click Calculate"
    );
    // Removed initial auto-calculation - now requires manual trigger
  }, []); // Empty dependency array ensures this only runs once

  const handleCalculate = async () => {
    console.log("🔍 [DEBUG] Starting handleCalculate");

    // Clear previous validation errors
    setValidationError(null);

    // Validate form before proceeding
    const isValid = await form.trigger();
    const errorMessages: string[] = [];

    if (!isValid) {
      const errors = form.formState.errors;
      console.log("🔍 [DEBUG] Form validation errors:", errors);

      // Check for specific required fields and create user-friendly messages
      // Note: We let the schema validation handle the error messages to avoid duplication

      // Add other validation errors, but only for fields that are actually invalid
      Object.entries(errors).forEach(([field, error]) => {
        if (error?.message && !errorMessages.includes(error.message)) {
          // Only add the error if the field is actually empty or invalid
          const fieldValue = form.getValues(field as keyof FormValues);
          if (
            fieldValue === "" ||
            fieldValue === undefined ||
            fieldValue === null
          ) {
            // Add more specific error message with field name
            errorMessages.push(`${field}: ${error.message}`);
          }
        }
      });
    }

    // Special validation for Veteran Type - only required for VA programs
    const mortgageType = form.getValues("mortgageType");
    const veteranType = form.getValues("veteranType");
    console.log("🔍 [DEBUG] Veteran Type validation:", {
      mortgageType,
      veteranType,
    });
    if (mortgageType === "VA" && (!veteranType || veteranType === "")) {
      console.log("🔍 [DEBUG] Adding Veteran Type validation error");
      errorMessages.push("Please select a Veteran Type for VA loans");
    }

    if (errorMessages.length > 0) {
      setValidationError(errorMessages.join(". "));
      return;
    }

    setIsCalculating(true);
    setApiError(null);
    setPricingResults([]); // Clear previous results when starting new calculation
    setCurrentTime(new Date());

    // Cancel any existing request
    if (abortControllerRef.current) {
      console.log("🔍 [DEBUG] Cancelling existing request");
      abortControllerRef.current.abort();
    }

    // Create new AbortController
    abortControllerRef.current = new AbortController();

    try {
      console.log("🔍 [DEBUG] Getting form values");
      const formValues = form.getValues();
      console.log("🔍 [DEBUG] Form values:", formValues);

      console.log("🔍 [DEBUG] Calling fetchPricingData");
      // Save request for debug
      setDebugRequest(null); // reset before fetch
      setDebugResponse(null);
      setDebugMapped(null);
      // Patch fetchPricingData to return request and response for debug
      const debugResults = await fetchPricingData(
        formValues,
        abortControllerRef.current.signal,
        (reqObj) => setDebugRequest(reqObj), // callback to get request
        (rawResp) => setDebugResponse(rawResp) // callback to get raw response
      );
      console.log("🔍 [DEBUG] Received results:", debugResults);

      // Filter out rates with 'No data available for this rate' and all-zero results
      let filteredResults = debugResults
        .map((entry: any) => ({
          ...entry,
          results: entry.results.filter(
            (r: any) =>
              r.alternativeProgram !== "No data available for this rate" &&
              // Exclude all-zero dummy results
              !(
                (r.finalRate === 0 || r.finalRate === undefined) &&
                (r.totalPayment === 0 || r.totalPayment === undefined) &&
                (r.price === 0 || r.price === undefined)
              )
          ),
        }))
        .filter((entry: any) => entry.results.length > 0);

      if (
        filteredResults.length === 0 ||
        filteredResults[0].results.length === 0
      ) {
        console.warn("⚠️ [DEBUG] No results returned from API");
        setApiError(
          "No pricing results available. Please try different criteria."
        );
        setPricingResults([]); // Clear previous results when no new results are found
      } else {
        console.log("🔍 [DEBUG] Setting pricing results:", filteredResults);
        setPricingResults(filteredResults);
        // Set optimal rate index instead of defaulting to 0
        const currentProgram = filteredResults[currentProgramIndex];
        if (currentProgram?.results) {
          setCurrentRateIndex(findOptimalRateIndex(currentProgram.results));
        } else {
          setCurrentRateIndex(0);
        }
        setIsResultsStale(false);
        setApiError(null); // Clear any previous error when results are found
        lastCalculatedValues.current = formValues;
        setActiveTab("qualified"); // Always force the Qualified tab after recalculation
        setHasCalculatedOnce(true); // Mark that user has manually calculated at least once
      }
      setDebugMapped({
        filteredResults,
        // Include error information if present
        ...(debugResults && (debugResults as any).debugAdjustments?.error
          ? { error: (debugResults as any).debugAdjustments.error }
          : {}),
      });
    } catch (error) {
      console.error("❌ [DEBUG] Error in handleCalculate:", error);
      if (error instanceof Error && error.name === "AbortError") {
        console.log("🔍 [DEBUG] Request was aborted");
        return;
      }
      const errorMessage =
        error instanceof Error
          ? error.message
          : "An error occurred while calculating pricing.";
      setApiError(errorMessage);
      setPricingResults([]); // Clear previous results when there's an error
      // Add error to debug info
      setDebugMapped((prev) => ({
        ...prev,
        error: {
          message: errorMessage,
          stack: error instanceof Error ? error.stack : undefined,
          timestamp: new Date().toISOString(),
        },
      }));
    } finally {
      console.log("🔍 [DEBUG] Finishing handleCalculate");
      setIsCalculating(false);
      abortControllerRef.current = null;
    }
  };

  const filterProgramOptions = (program: string): boolean =>
    getPrograms().some((p: DropdownOption) => p.value === program);

  const filterIncomeTypeOptions = (type: string): boolean =>
    availableIncomeTypes.some((t: DropdownOption) => t.value === type);

  const filterCitizenshipOptions = (option: DropdownOption): boolean =>
    availableCitizenshipOptions.some(
      (c: DropdownOption) => c.value === option.value
    );

  const filterOccupancyOptions = (option: DropdownOption): boolean =>
    availableOccupancyOptions.some(
      (o: DropdownOption) => o.value === option.value
    );

  const filterPurposeOptions = (option: DropdownOption): boolean =>
    availablePurposeOptions.some(
      (p: DropdownOption) => p.value === option.value
    );

  const filterPropertyTypeOptions = (option: DropdownOption): boolean =>
    availablePropertyTypeOptions.some(
      (p: DropdownOption) => p.value === option.value
    );

  const filterNumberOfUnitsOptions = (option: DropdownOption): boolean =>
    availableNumberOfUnitsOptions.some(
      (n: DropdownOption) => n.value === option.value
    );

  const filterLockPeriodOptions = (option: DropdownOption): boolean =>
    availableLockPeriodOptions.some(
      (l: DropdownOption) => l.value === option.value
    );

  const filterCreditEventOptions = (option: DropdownOption): boolean =>
    availableCreditEventOptions.some(
      (c: DropdownOption) => c.value === option.value
    );

  const filterPrepaymentPenaltyOptions = (option: DropdownOption): boolean => {
    const program = form.getValues("program");
    const occupancy = form.getValues("occupancy");
    const incomeType = form.getValues("incomeType");
    const propertyType = form.getValues("propertyType");
    const numberOfUnits = form.getValues("numberOfUnits");

    if (!program || !occupancy || !incomeType) return false;

    return getPrepaymentPenaltyOptions(
      program,
      occupancy,
      incomeType,
      propertyType,
      numberOfUnits
    ).some((p: DropdownOption) => p.value === option.value);
  };

  // Add effect to calculate underwriting fee percentage when relevant fields change
  useEffect(() => {
    const currentValues = form.getValues();
    if (currentValues.lenderFeeBuyOut && currentValues.loanAmount > 0) {
      const percentage = calculateUnderwritingFeePercentage(
        {
          ...currentValues,
          incomeDocumentation: "Full Doc", // Add default value for required field
        },
        currentValues.loanAmount
      );
      setUnderwritingFeePercentage(percentage);
      if (percentage !== null) {
        form.setValue("underwritingFeePercentage", percentage);
      }
    } else {
      setUnderwritingFeePercentage(null);
      form.setValue("underwritingFeePercentage", 0);
    }
  }, [
    form.watch("lenderFeeBuyOut"),
    form.watch("loanAmount"),
    form.watch("program"),
  ]);

  const generateShareableUrl = () => {
    const baseUrl = window.location.origin;
    const queryParams = new URLSearchParams();
    const currentValues = form.getValues();

    // Add key parameters to the URL
    queryParams.append("program", currentValues.program);
    if (currentValues.incomeType) {
      queryParams.append("incomeType", currentValues.incomeType);
    }
    queryParams.append("fico", currentValues.fico.toString());
    queryParams.append("ltv", currentValues.ltv.toString());
    queryParams.append("loanAmount", currentValues.loanAmount.toString());
    queryParams.append("programIndex", currentProgramIndex.toString());
    queryParams.append("rateIndex", currentRateIndex.toString());

    return `${baseUrl}?${queryParams.toString()}`;
  };

  const getShareTitle = () => {
    if (!currentProduct) return "";
    return `theLender.com Mortgage Quote: ${currentProduct.finalRate.toFixed(
      3
    )}%`;
  };

  const getShareText = () => {
    if (!currentProduct) return "";
    return `Check out this mortgage quote from theLender.com: ${currentProduct.finalRate.toFixed(
      3
    )}% rate with a monthly payment of ${formatCurrency(monthlyPayment)}.`;
  };

  // Add this above the return statement in the component

  // Restore useWatch hooks for broker comp logic
  const watchedLoanAmount = useWatch({
    control: form.control,
    name: "loanAmount",
  });
  const watchedBrokerComp = useWatch({
    control: form.control,
    name: "brokerCompensation",
  });
  const watchedBrokerCompValue = useWatch({
    control: form.control,
    name: "brokerCompensationValue",
  });

  // Compute filteredNumberOfUnitsOptions based on property type
  let filteredNumberOfUnitsOptions = availableNumberOfUnitsOptions;
  let isNumberOfUnitsReadOnly = false;

  if (watchedPropertyType === "UnitDwelling_2_4") {
    // Only 2, 3, 4 units
    filteredNumberOfUnitsOptions = availableNumberOfUnitsOptions.filter((opt) =>
      ["2", "3", "4"].includes(opt.value)
    );
  } else if (watchedPropertyType === "2-8 Mixed Use") {
    // Only 2 and above
    filteredNumberOfUnitsOptions = availableNumberOfUnitsOptions.filter(
      (opt) => !isNaN(Number(opt.value)) && Number(opt.value) >= 2
    );
  } else if (watchedPropertyType === "MultiFamily") {
    // Only 5 and above
    filteredNumberOfUnitsOptions = availableNumberOfUnitsOptions.filter(
      (opt) => !isNaN(Number(opt.value)) && Number(opt.value) >= 5
    );
  } else {
    // All other property types: read-only, 1-Unit
    filteredNumberOfUnitsOptions = availableNumberOfUnitsOptions.filter(
      (opt) => opt.value === "1"
    );
    isNumberOfUnitsReadOnly = true;
  }

  // useEffect to set numberOfUnits value when property type or options change
  React.useEffect(() => {
    if (watchedPropertyType === "UnitDwelling_2_4") {
      if (
        !filteredNumberOfUnitsOptions.some(
          (opt) => opt.value === form.getValues("numberOfUnits")
        )
      ) {
        form.setValue("numberOfUnits", "2");
      }
    } else if (watchedPropertyType === "2-8 Mixed Use") {
      if (
        !filteredNumberOfUnitsOptions.some(
          (opt) => opt.value === form.getValues("numberOfUnits")
        )
      ) {
        form.setValue(
          "numberOfUnits",
          filteredNumberOfUnitsOptions[0]?.value || "2"
        );
      }
    } else if (watchedPropertyType === "MultiFamily") {
      if (
        !filteredNumberOfUnitsOptions.some(
          (opt) => opt.value === form.getValues("numberOfUnits")
        )
      ) {
        form.setValue(
          "numberOfUnits",
          filteredNumberOfUnitsOptions[0]?.value || "5"
        );
      }
    } else {
      if (form.getValues("numberOfUnits") !== "1") {
        form.setValue("numberOfUnits", "1");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedPropertyType, availableNumberOfUnitsOptions]);

  useEffect(() => {
    const hasQualified = pricingResults.some((p) => !p.isDisqualified);
    const hasDisqualified = pricingResults.some((p) => p.isDisqualified);

    // Only auto-switch if there are no results in the current tab
    if (activeTab === "qualified" && !hasQualified && hasDisqualified) {
      // Switch to non-qualified tab if there are no qualified results but there are disqualified results
      setActiveTab("non-qualified");
    } else if (
      activeTab === "non-qualified" &&
      !hasDisqualified &&
      hasQualified
    ) {
      // Switch to qualified tab if there are no disqualified results but there are qualified results
      setActiveTab("qualified");
    }
  }, [pricingResults, activeTab]);

  // Cleanup timeout and abort controller on unmount
  useEffect(() => {
    return () => {
      if (autoCalculationTimeout) {
        clearTimeout(autoCalculationTimeout);
      }
      if (
        abortControllerRef.current &&
        typeof abortControllerRef.current.abort === "function" &&
        !abortControllerRef.current.signal.aborted
      ) {
        try {
          abortControllerRef.current.abort();
        } catch (error) {
          // Ignore abort errors as they are expected when cancelling requests
          console.log("Abort controller cleanup:", error);
        }
      }
    };
  }, [autoCalculationTimeout]);

  // Add effect to handle Interest Only logic for 40yr ARM
  useEffect(() => {
    // 40yr ARM values from program-config: "40-7-6-ARM", "40-10-6-ARM"
    const is40YrARM =
      form.getValues("amortizationType") === "40-7-6-ARM" ||
      form.getValues("amortizationType") === "40-10-6-ARM";
    if (is40YrARM) {
      form.setValue("interestOnly", true);
      setInterestOnlyReadOnly(true);
    } else {
      setInterestOnlyReadOnly(false);
    }
  }, [form.getValues("amortizationType"), form]);

  // Ensure broker comp amount input is hidden if not LPC or if DSCR is selected
  useEffect(() => {
    if (
      form.getValues("brokerCompensation") !== "LPC" ||
      watchedIncomeType?.includes("DSCR")
    ) {
      setShowBrokerCompAmount(false);
    }
  }, [watchedIncomeType, form.watch("brokerCompensation")]);

  // Save LPC value to session storage whenever it changes
  useEffect(() => {
    const brokerCompensation = form.watch("brokerCompensation");
    const brokerCompensationValue = form.watch("brokerCompensationValue");

    if (brokerCompensation === "LPC" && brokerCompensationValue > 0) {
      saveLPCValue(brokerCompensationValue);
    }
  }, [form.watch("brokerCompensation"), form.watch("brokerCompensationValue")]);

  return (
    <div className="min-h-screen bg-white py-4">
      <div className="max-w-7xl mx-auto px-4">
        {/* Top section - spans full width */}
        <Card className="border-0 mb-4">
          <CardContent className="p-4">
            <Form {...form}>
              <form>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                  <FormField
                    control={form.control}
                    name="program"
                    render={({
                      field,
                    }: {
                      field: ControllerRenderProps<FormValues, "program">;
                    }) => (
                      <FormItem>
                        <FormLabel>Program</FormLabel>
                        <Select
                          onValueChange={(value: string) =>
                            handleFieldChange("program", value)
                          }
                          value={field.value || "Non-QM"}
                          defaultValue="Non-QM"
                        >
                          <FormControl>
                            <SelectTrigger
                              className={`h-8 text-sm bg-transparent border-0 border-b rounded-none px-0 focus:ring-0 focus:border-primary ${
                                form.formState.errors.program
                                  ? "border-red-500 bg-red-50"
                                  : "border-gray-300"
                              }`}
                            >
                              <SelectValue placeholder="Select program" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {getGroupedPrograms().map((group: any) =>
                              group.label ? (
                                <SelectGroup key={group.label}>
                                  <SelectLabel>{group.label}</SelectLabel>
                                  {group.options.map((item: any) => (
                                    <SelectItem
                                      key={`${group.label}:${item.value}`}
                                      value={`${group.label}:${item.value}`}
                                      className="ml-6"
                                    >
                                      {item.label || item.value}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              ) : (
                                // For groups without labels (Conventional and Non-QM), render options directly
                                group.options.map((item: any) => (
                                  <SelectItem
                                    key={item.value}
                                    value={item.value}
                                  >
                                    {item.label || item.value}
                                  </SelectItem>
                                ))
                              )
                            )}
                          </SelectContent>
                        </Select>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                  {availableLoanTypes.length > 0 && (
                    <FormField
                      control={form.control}
                      name="loanType"
                      render={({
                        field,
                      }: {
                        field: ControllerRenderProps<FormValues, "loanType">;
                      }) => (
                        <FormItem
                          style={{
                            display:
                              availableLoanTypes.length === 0
                                ? "none"
                                : "block",
                          }}
                        >
                          <FormLabel>Loan Type</FormLabel>
                          <Select
                            onValueChange={(value: string) =>
                              handleFieldChange("loanType", value)
                            }
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger
                                className={`h-8 text-sm bg-transparent border-0 border-b rounded-none px-0 focus:ring-0 focus:border-primary ${
                                  form.formState.errors.loanType
                                    ? "border-red-500 bg-red-50"
                                    : "border-gray-300"
                                }`}
                              >
                                <SelectValue placeholder="Select loan type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {availableLoanTypes.map(
                                (loanType: DropdownOption) => (
                                  <SelectItem
                                    key={loanType.value}
                                    value={loanType.value}
                                  >
                                    {loanType.label || loanType.value}
                                  </SelectItem>
                                )
                              )}
                            </SelectContent>
                          </Select>
                          {availableLoanTypes.length > 0 && (
                            <FormMessage className="text-xs" />
                          )}
                        </FormItem>
                      )}
                    />
                  )}
                  {availableIncomeTypes.length > 0 && (
                    <FormField
                      control={form.control}
                      name="incomeType"
                      render={({
                        field,
                      }: {
                        field: ControllerRenderProps<FormValues, "incomeType">;
                      }) => (
                        <FormItem
                          style={{
                            display:
                              availableIncomeTypes.length === 0
                                ? "none"
                                : "block",
                          }}
                        >
                          <FormLabel>Income Type</FormLabel>
                          <Select
                            onValueChange={(value: string) =>
                              handleFieldChange("incomeType", value)
                            }
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger
                                className={`h-8 text-sm bg-transparent border-0 border-b rounded-none px-0 focus:ring-0 focus:border-primary ${
                                  form.formState.errors.incomeType
                                    ? "border-red-500 bg-red-50"
                                    : "border-gray-300"
                                }`}
                              >
                                <SelectValue placeholder="Select income type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {availableIncomeTypes.map(
                                (type: DropdownOption) => (
                                  <SelectItem
                                    key={type.value}
                                    value={type.value}
                                  >
                                    {type.label}
                                  </SelectItem>
                                )
                              )}
                            </SelectContent>
                          </Select>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />
                  )}
                  {availableCitizenshipOptions.length > 0 && (
                    <FormField
                      control={form.control}
                      name="citizenship"
                      render={({
                        field,
                      }: {
                        field: ControllerRenderProps<FormValues, "citizenship">;
                      }) => (
                        <FormItem
                          style={{
                            display:
                              availableCitizenshipOptions.length === 0
                                ? "none"
                                : "block",
                          }}
                        >
                          <FormLabel>Citizenship</FormLabel>
                          <Select
                            onValueChange={(value: string) =>
                              handleFieldChangeWithRecalculate(
                                "citizenship",
                                value
                              )
                            }
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger
                                className={`h-8 text-sm bg-transparent border-0 border-b rounded-none px-0 focus:ring-0 focus:border-primary ${
                                  form.formState.errors.citizenship
                                    ? "border-red-500 bg-red-50"
                                    : "border-gray-300"
                                }`}
                              >
                                <SelectValue placeholder="Select citizenship" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {availableCitizenshipOptions.map(
                                (option: DropdownOption) => (
                                  <SelectItem
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </SelectItem>
                                )
                              )}
                            </SelectContent>
                          </Select>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />
                  )}
                  {availableOccupancyOptions.length > 0 && (
                    <FormField
                      control={form.control}
                      name="occupancy"
                      render={({
                        field,
                      }: {
                        field: ControllerRenderProps<FormValues, "occupancy">;
                      }) => (
                        <FormItem
                          style={{
                            display:
                              availableOccupancyOptions.length === 0
                                ? "none"
                                : "block",
                          }}
                        >
                          <FormLabel>Occupancy</FormLabel>
                          <Select
                            onValueChange={(value: string) =>
                              handleFieldChangeWithRecalculate(
                                "occupancy",
                                value
                              )
                            }
                            value={field.value}
                            disabled={isOccupancyDisabled}
                          >
                            <FormControl>
                              <SelectTrigger
                                className={`h-8 text-sm bg-transparent border-0 border-b rounded-none px-0 focus:ring-0 focus:border-primary ${
                                  form.formState.errors.occupancy
                                    ? "border-red-500 bg-red-50"
                                    : "border-gray-300"
                                } ${
                                  isOccupancyDisabled
                                    ? "bg-gray-100 cursor-not-allowed text-gray-500"
                                    : ""
                                }`}
                              >
                                <SelectValue placeholder="Select occupancy" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {availableOccupancyOptions.map(
                                (option: DropdownOption) => (
                                  <SelectItem
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </SelectItem>
                                )
                              )}
                            </SelectContent>
                          </Select>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />
                  )}
                  {availablePurposeOptions.length > 0 && (
                    <FormField
                      control={form.control}
                      name="purpose"
                      render={({
                        field,
                      }: {
                        field: ControllerRenderProps<FormValues, "purpose">;
                      }) => (
                        <FormItem
                          style={{
                            display:
                              availablePurposeOptions.length === 0
                                ? "none"
                                : "block",
                          }}
                        >
                          <FormLabel>Purpose</FormLabel>
                          <Select
                            onValueChange={(value: string) =>
                              handleFieldChangeWithRecalculate("purpose", value)
                            }
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger
                                className={`h-8 text-sm bg-transparent border-0 border-b rounded-none px-0 focus:ring-0 focus:border-primary ${
                                  form.formState.errors.purpose
                                    ? "border-red-500 bg-red-50"
                                    : "border-gray-300"
                                }`}
                              >
                                <SelectValue placeholder="Select purpose" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {availablePurposeOptions.map(
                                (option: DropdownOption) => (
                                  <SelectItem
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </SelectItem>
                                )
                              )}
                            </SelectContent>
                          </Select>
                          <FormMessage className="text-xs" />
                        </FormItem>
                      )}
                    />
                  )}
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>

        {/* Bottom section - results and other fields */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Results section - last on mobile, first on desktop */}
          <div className="lg:col-span-1 order-2 lg:order-1">
            {/* Validation Error Message */}
            {validationError && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-red-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Please complete the following required fields:
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      {validationError}
                    </div>
                  </div>
                </div>
              </div>
            )}
            {currentResults.length > 0 ? (
              <div className="relative mt-8 mx-auto">
                <Card className="border-0 bg-[#F4F4F4] text-black relative">
                  <CardContent className="p-8">
                    {isCalculating ? (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-10">
                        <div className="w-12 h-12 border-4 border-gray-400 border-t-white rounded-full animate-spin"></div>
                      </div>
                    ) : null}

                    {/* Auto-calculation indicator */}
                    {isAutoCalculating && (
                      <div className="flex items-center justify-center mb-4 text-sm text-[#999999]">
                        <div className="w-3 h-3 border-2 border-[#999999] border-t-transparent rounded-full animate-spin mr-2"></div>
                        <span>Recalculating...</span>
                      </div>
                    )}

                    <div
                      className={`space-y-6 ${
                        isResultsStale ? "opacity-50" : ""
                      }`}
                    >
                      {/* Tab Navigation */}
                      <div className="flex border-b border-gray-300">
                        <button
                          onClick={() => {
                            if (
                              pricingResults.filter((p) => !p.isDisqualified)
                                .length === 0
                            )
                              return;
                            setActiveTab("qualified");
                          }}
                          className={`flex-1 py-2 px-4 text-sm font-medium border-b-2 transition-colors ${
                            activeTab === "qualified"
                              ? "border-green-500 text-green-600"
                              : "border-transparent text-gray-500 hover:text-gray-700"
                          } ${
                            pricingResults.filter((p) => !p.isDisqualified)
                              .length === 0
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                          disabled={
                            pricingResults.filter((p) => !p.isDisqualified)
                              .length === 0
                          }
                          style={
                            pricingResults.filter((p) => !p.isDisqualified)
                              .length === 0
                              ? { opacity: 0.5, cursor: "not-allowed" }
                              : {}
                          }
                        >
                          Eligible (
                          {
                            pricingResults.filter((p) => !p.isDisqualified)
                              .length
                          }
                          )
                        </button>
                        <button
                          onClick={() => {
                            if (
                              pricingResults.filter((p) => p.isDisqualified)
                                .length === 0
                            )
                              return;
                            setActiveTab("non-qualified");
                          }}
                          className={`flex-1 py-2 px-4 text-sm font-medium border-b-2 transition-colors ${
                            activeTab === "non-qualified"
                              ? "border-red-500 text-red-600"
                              : "border-transparent text-gray-500 hover:text-gray-700"
                          } ${
                            pricingResults.filter((p) => p.isDisqualified)
                              .length === 0
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                          disabled={
                            pricingResults.filter((p) => p.isDisqualified)
                              .length === 0
                          }
                          style={
                            pricingResults.filter((p) => p.isDisqualified)
                              .length === 0
                              ? { opacity: 0.5, cursor: "not-allowed" }
                              : {}
                          }
                        >
                          Non-Eligible (
                          {
                            pricingResults.filter((p) => p.isDisqualified)
                              .length
                          }
                          )
                        </button>
                      </div>

                      {/* Program Selection */}
                      {currentPrograms.length > 1 ? (
                        <div className="flex justify-center items-center">
                          <Select
                            value={currentProgramIndex.toString()}
                            onValueChange={(value) => {
                              const newProgramIndex = parseInt(value);
                              setCurrentProgramIndex(newProgramIndex);
                              // Set optimal rate for the selected program
                              const selectedProgram =
                                currentPrograms[newProgramIndex];
                              if (selectedProgram?.results) {
                                setCurrentRateIndex(
                                  findOptimalRateIndex(selectedProgram.results)
                                );
                              } else {
                                setCurrentRateIndex(0);
                              }
                            }}
                          >
                            <SelectTrigger className="w-full max-w-xs">
                              <SelectValue placeholder="Select program" />
                            </SelectTrigger>
                            <SelectContent>
                              {currentPrograms.map((program, index) => (
                                <SelectItem
                                  key={index}
                                  value={index.toString()}
                                >
                                  {program.programName}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : currentPrograms.length === 1 ? (
                        <div className="flex justify-center items-center py-2">
                          <span className="text-lg font-semibold text-gray-700">
                            {currentPrograms[0].programName}
                          </span>
                        </div>
                      ) : null}

                      {/* Navigation Buttons */}

                      {/* Rate and Payment Information - Hide when no qualified results and on disqualified tab */}
                      {!(
                        pricingResults.filter((p) => !p.isDisqualified)
                          .length === 0 && activeTab === "non-qualified"
                      ) && (
                        <div className="text-center">
                          <div className="mb-4 flex items-center justify-center gap-4">
                            {currentResults.length > 1 && (
                              <button
                                onClick={goToPreviousRate}
                                disabled={currentRateIndex === 0}
                                className="text-red-500 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="w-6 h-6"
                                >
                                  <path d="m18 15-6-6-6 6" />
                                </svg>
                              </button>
                            )}
                            <h3
                              ref={rateDisplayRef}
                              key={`rate-${currentRateIndex}-${currentProgramIndex}-${forceUpdate}`}
                              className={`text-4xl font-oswald ${
                                activeTab === "non-qualified"
                                  ? "text-gray-400"
                                  : ""
                              }`}
                            >
                              {(() => {
                                console.log(
                                  "🔍 UI RENDERING - displayProduct:",
                                  displayProduct
                                );
                                console.log(
                                  "🔍 UI RENDERING - finalRate:",
                                  displayProduct?.finalRate
                                );
                                console.log(
                                  "🔍 UI RENDERING - currentRateIndex:",
                                  currentRateIndex
                                );
                                return (
                                  displayProduct?.finalRate?.toFixed(3) || "N/A"
                                );
                              })()}
                              %
                            </h3>
                            {currentResults.length > 1 && (
                              <button
                                onClick={goToNextRate}
                                disabled={
                                  currentRateIndex === currentResults.length - 1
                                }
                                className="text-red-500 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="w-6 h-6"
                                >
                                  <path d="m6 9 6 6 6-6" />
                                </svg>
                              </button>
                            )}
                          </div>
                          <div className="mb-4">
                            <div className="flex justify-center items-center gap-12">
                              <div>
                                <h4
                                  ref={paymentDisplayRef}
                                  key={`payment-${currentRateIndex}-${currentProgramIndex}-${forceUpdate}`}
                                  className={`text-3xl font-oswald ${
                                    activeTab === "non-qualified"
                                      ? "text-gray-400"
                                      : ""
                                  }`}
                                >
                                  {formatCurrency(monthlyPayment)}
                                </h4>
                                <p className="text-sm text-gray-400">
                                  Monthly Payment
                                </p>
                              </div>
                              {displayProduct ? (
                                (() => {
                                  let priceDisplay = "N/A";
                                  console.log(
                                    "Display Product:",
                                    displayProduct
                                  );

                                  if (
                                    typeof displayProduct?.price === "number"
                                  ) {
                                    priceDisplay =
                                      displayProduct.price > 100
                                        ? "Price/Credit"
                                        : displayProduct.price === 100
                                        ? "Price"
                                        : "Price/Discount";
                                  }
                                  return (
                                    <div className="flex flex-col items-center">
                                      <h4
                                        className={`text-3xl font-oswald ${
                                          activeTab === "non-qualified"
                                            ? "text-gray-400"
                                            : ""
                                        }`}
                                      >
                                        {displayProduct?.price?.toFixed(3) ||
                                          "N/A"}
                                      </h4>
                                      <p className="text-sm text-gray-400">
                                        {priceDisplay}
                                      </p>
                                    </div>
                                  );
                                })()
                              ) : (
                                <div className="flex flex-col items-center">
                                  <h4 className="text-3xl font-oswald text-gray-400">
                                    N/A
                                  </h4>
                                  <p className="text-sm text-gray-400">
                                    No data available
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Display disqualification reasons - only show on non-qualified tab */}
                      {activeTab === "non-qualified" &&
                        displayProduct?.disqualifyAdjustments &&
                        displayProduct.disqualifyAdjustments.length > 0 && (
                          <div className="mt-4 text-left">
                            <h4 className="text-lg font-semibold text-black mb-2">
                              Disqualification Reasons:
                            </h4>
                            <div className="space-y-1">
                              {[
                                ...new Set(
                                  displayProduct.disqualifyAdjustments
                                ),
                              ].map((reason, index) => (
                                <div key={index} className="text-sm text-black">
                                  • {reason}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                      {/* Program Details */}
                      <div className="space-y-3 text-sm">
                        {/* Removed Amortization and Lock Period fields */}
                      </div>

                      {/* Action Buttons */}
                      <div className="space-y-3">
                        {activeTab === "qualified" && (
                          <>
                            <Button
                              onClick={() => setIsSharePopupOpen(true)}
                              className="w-full bg-[#E6002B] hover:bg-[#cc0027] text-white font-medium py-3 px-8"
                            >
                              <div className="flex items-center justify-center">
                                <Download className="w-4 h-4 mr-2" />
                                <span>DOWNLOAD QUOTE</span>
                              </div>
                            </Button>
                            <Button
                              onClick={() => setIsRateStackPopupOpen(true)}
                              variant="outline"
                              className="w-full border-gray-300 text-gray-700 hover:bg-gray-50 font-medium py-2 px-4"
                            >
                              <span>CLICK FOR FULL RATE STACK</span>
                            </Button>
                            {displayProduct?.allAdjustments &&
                              displayProduct.allAdjustments.length > 0 && (
                                <Button
                                  onClick={() =>
                                    setIsAdjustmentsPopupOpen(true)
                                  }
                                  variant="outline"
                                  className="w-full border-gray-300 text-gray-700 hover:bg-gray-50 font-medium py-2 px-4"
                                >
                                  <span>SHOW ADJUSTMENTS</span>
                                </Button>
                              )}
                          </>
                        )}
                      </div>

                      {/* Quote Time */}
                      <div className="text-center text-xs text-gray-400">
                        Quote as of {formatDate(currentTime)} at{" "}
                        {formatTime(currentTime)}
                      </div>

                      {/* Disclaimer */}
                      <div className="text-center text-xs text-gray-500 mt-3 px-4">
                        This pricing tool is for informational purposes only and
                        does not constitute a loan commitment. Rates and terms
                        are subject to change without notice. Contact theLender
                        for the most current information.
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="relative mt-8 mx-auto">
                <Card className="border-0 bg-[#F4F4F4] text-black relative">
                  <CardContent className="p-8">
                    <div className="flex flex-col items-center justify-center text-center space-y-3">
                      {isCalculating ? (
                        <div className="w-12 h-12 border-4 border-gray-400 border-t-white rounded-full animate-spin"></div>
                      ) : apiError ? (
                        // Show the same message as the initial state if apiError is the no results error
                        apiError ===
                        "No pricing results available. Please try different criteria." ? (
                          <>
                            <Calculator className="w-8 h-8 text-gray-400" />
                            <h3 className="text-sm font-medium text-black">
                              No loan programs found
                            </h3>
                            <p className="text-xs text-gray-400">
                              Adjust your loan details and click Calculate to
                              see available options.
                            </p>
                          </>
                        ) : (
                          <>
                            <Calculator className="w-8 h-8 text-gray-400" />
                            <h3 className="text-sm font-medium text-black">
                              Error
                            </h3>
                            <p className="text-xs text-red-500 bg-red-900/20 p-3">
                              {apiError}
                            </p>
                          </>
                        )
                      ) : currentPrograms.length > 0 ? (
                        // Show tab-specific message when there are results but none in current tab
                        <>
                          <Calculator className="w-8 h-8 text-gray-400" />
                          <h3 className="text-sm font-medium text-black">
                            No{" "}
                            {activeTab === "qualified"
                              ? "qualified"
                              : "non-qualified"}{" "}
                            programs found
                          </h3>
                          <p className="text-xs text-gray-400">
                            {activeTab === "qualified"
                              ? "All available programs have disqualifications. Check the Non-Eligible tab for details."
                              : "All available programs are eligible. Check the eligible tab for options."}
                          </p>
                        </>
                      ) : !hasCalculatedOnce ? (
                        // Show Calculate button for first-time users
                        <>
                          <Calculator className="w-8 h-8 text-gray-400" />
                          <button
                            type="button"
                            onClick={handleCalculate}
                            disabled={isCalculating}
                            className="px-8 py-3 bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {isCalculating ? (
                              <div className="flex items-center">
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                Calculating...
                              </div>
                            ) : (
                              "Calculate"
                            )}
                          </button>
                        </>
                      ) : pricingResults.length > 0 && isResultsStale ? (
                        // Show previous results with recalculating message when inputs are changing
                        <>
                          <Calculator className="w-8 h-8 text-gray-400" />
                          <h3 className="text-sm font-medium text-black">
                            Recalculating...
                          </h3>
                          <p className="text-xs text-gray-400">
                            Previous results are being updated with new inputs.
                          </p>
                        </>
                      ) : (
                        <>
                          <Calculator className="w-8 h-8 text-gray-400" />
                          <h3 className="text-sm font-medium text-black">
                            No loan programs found
                          </h3>
                          <p className="text-xs text-gray-400">
                            Adjust your loan details and click Calculate to see
                            available options.
                          </p>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>

          {/* Input fields section - first on mobile, second on desktop */}
          <div className="lg:col-span-2 order-1 lg:order-2">
            <Card className="border-0">
              <CardContent className="p-4">
                <Form {...form}>
                  <form>
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="fico"
                        render={({
                          field,
                        }: {
                          field: ControllerRenderProps<FormValues, "fico">;
                        }) => (
                          <FormItem className="w-full">
                            <FormLabel>FICO</FormLabel>
                            <input
                              type="number"
                              {...field}
                              onChange={(e) => {
                                const value = parseInt(e.target.value) || 0;
                                // Enforce FICO range
                                const clampedValue = Math.min(
                                  Math.max(value, ficoRange.min),
                                  ficoRange.max
                                );
                                handleFieldChangeNoAutoCalc(
                                  "fico",
                                  clampedValue
                                );
                              }}
                              onBlur={() => handleFieldBlur("fico")}
                              min={ficoRange.min}
                              max={ficoRange.max}
                              className={`w-full h-8 px-0 text-sm bg-transparent border-0 border-b focus:outline-none focus:border-primary focus:ring-0 ${
                                form.formState.errors.fico
                                  ? "border-red-500 bg-red-50"
                                  : "border-gray-300"
                              }`}
                            />
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="ltv"
                        render={({
                          field,
                        }: {
                          field: ControllerRenderProps<FormValues, "ltv">;
                        }) => (
                          <FormItem className="w-full">
                            <FormLabel>
                              {watchedProgram === "Second Lien"
                                ? "CLTV"
                                : "LTV"}
                            </FormLabel>
                            <input
                              type="number"
                              {...field}
                              onChange={(e) => {
                                const value = parseInt(e.target.value) || 0;
                                // Enforce LTV range
                                const clampedValue = Math.min(
                                  Math.max(value, ltvRange.min),
                                  ltvRange.max
                                );
                                handleFieldChangeNoAutoCalc(
                                  "ltv",
                                  clampedValue
                                );
                              }}
                              onBlur={() => handleFieldBlur("ltv")}
                              min={ltvRange.min}
                              max={ltvRange.max}
                              className={`w-full h-8 px-0 text-sm bg-transparent border-0 border-b focus:outline-none focus:border-primary focus:ring-0 ${
                                form.formState.errors.ltv
                                  ? "border-red-500 bg-red-50"
                                  : "border-gray-300"
                              }`}
                            />
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="loanAmount"
                        render={({
                          field,
                        }: {
                          field: ControllerRenderProps<
                            FormValues,
                            "loanAmount"
                          >;
                        }) => (
                          <FormItem className="w-full">
                            <FormLabel>Loan Amount</FormLabel>
                            <div className="relative w-full">
                              <span className="absolute left-0 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                                $
                              </span>
                              <input
                                type="number"
                                {...field}
                                onChange={(e) => {
                                  const value = parseInt(e.target.value) || 0;
                                  // Enforce Loan Amount range
                                  const clampedValue = Math.min(
                                    Math.max(value, loanAmountRange.min),
                                    loanAmountRange.max
                                  );
                                  handleFieldChangeNoAutoCalc(
                                    "loanAmount",
                                    clampedValue
                                  );
                                }}
                                onBlur={() => handleFieldBlur("loanAmount")}
                                min={loanAmountRange.min}
                                max={loanAmountRange.max}
                                className={`w-full h-8 pl-4 pr-0 text-sm bg-transparent border-0 border-b focus:outline-none focus:border-primary focus:ring-0 ${
                                  form.formState.errors.loanAmount
                                    ? "border-red-500 bg-red-50"
                                    : "border-gray-300"
                                }`}
                              />
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="amortizationType"
                        render={({
                          field,
                        }: {
                          field: ControllerRenderProps<
                            FormValues,
                            "amortizationType"
                          >;
                        }) => (
                          <FormItem
                            style={{
                              display:
                                availableAmortizationTypes.length === 0
                                  ? "none"
                                  : "block",
                            }}
                          >
                            <FormLabel>Amortization Type</FormLabel>
                            <Select
                              onValueChange={(value: string) =>
                                handleFieldChangeWithRecalculate(
                                  "amortizationType",
                                  value
                                )
                              }
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger
                                  className={`h-8 text-sm bg-transparent border-0 border-b rounded-none px-0 focus:ring-0 focus:border-primary ${
                                    form.formState.errors.amortizationType
                                      ? "border-red-500 bg-red-50"
                                      : "border-gray-300"
                                  }`}
                                >
                                  <SelectValue placeholder="Select amortization type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {availableAmortizationTypes.map(
                                  (type: DropdownOption) => (
                                    <SelectItem
                                      key={type.value}
                                      value={type.value}
                                    >
                                      {type.label}
                                    </SelectItem>
                                  )
                                )}
                              </SelectContent>
                            </Select>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="propertyType"
                        render={({
                          field,
                        }: {
                          field: ControllerRenderProps<
                            FormValues,
                            "propertyType"
                          >;
                        }) => (
                          <FormItem
                            style={{
                              display:
                                availablePropertyTypeOptions.length === 0
                                  ? "none"
                                  : "block",
                            }}
                          >
                            <FormLabel>Property Type</FormLabel>
                            <Select
                              onValueChange={(value: string) => {
                                handleFieldChangeWithRecalculate(
                                  "propertyType",
                                  value
                                );
                                if (
                                  value === "2-8 Mixed Use" ||
                                  value === "MultiFamily"
                                ) {
                                  form.setValue("numberOfUnits", "5");
                                } else if (value === "UnitDwelling_2_4") {
                                  form.setValue("numberOfUnits", "2");
                                }
                              }}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger
                                  className={`h-8 text-sm bg-transparent border-0 border-b rounded-none px-0 focus:ring-0 focus:border-primary ${
                                    form.formState.errors.propertyType
                                      ? "border-red-500 bg-red-50"
                                      : "border-gray-300"
                                  }`}
                                >
                                  <SelectValue placeholder="Select property type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {availablePropertyTypeOptions.map(
                                  (option: DropdownOption) => (
                                    <SelectItem
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </SelectItem>
                                  )
                                )}
                              </SelectContent>
                            </Select>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="vesting"
                        render={({
                          field,
                        }: {
                          field: ControllerRenderProps<FormValues, "vesting">;
                        }) => (
                          <FormItem
                            style={{
                              display:
                                availableVestingOptions.length === 0
                                  ? "none"
                                  : "block",
                            }}
                          >
                            <FormLabel>Vesting</FormLabel>
                            <Select
                              onValueChange={(value: string) =>
                                handleFieldChangeWithRecalculate(
                                  "vesting",
                                  value
                                )
                              }
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger
                                  className={`h-8 text-sm bg-transparent border-0 border-b rounded-none px-0 focus:ring-0 focus:border-primary ${
                                    form.formState.errors.vesting
                                      ? "border-red-500 bg-red-50"
                                      : "border-gray-300"
                                  }`}
                                >
                                  <SelectValue placeholder="Select vesting" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {availableVestingOptions.map((option) => (
                                  <SelectItem
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {availableMortgageHistoryOptions.length > 0 && (
                        <FormField
                          control={form.control}
                          name="mortgageHistory"
                          render={({
                            field,
                          }: {
                            field: ControllerRenderProps<
                              FormValues,
                              "mortgageHistory"
                            >;
                          }) => (
                            <FormItem>
                              <FormLabel>Mortgage History</FormLabel>
                              <Select
                                onValueChange={(value: string) =>
                                  handleFieldChangeWithRecalculate(
                                    "mortgageHistory",
                                    value
                                  )
                                }
                                value={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger
                                    className={`h-8 text-sm bg-transparent border-0 border-b rounded-none px-0 focus:ring-0 focus:border-primary ${
                                      form.formState.errors.mortgageHistory
                                        ? "border-red-500 bg-red-50"
                                        : "border-gray-300"
                                    }`}
                                  >
                                    <SelectValue placeholder="Select mortgage history" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {availableMortgageHistoryOptions.map(
                                    (option) => (
                                      <SelectItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </SelectItem>
                                    )
                                  )}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}

                      <FormField
                        control={form.control}
                        name="numberOfUnits"
                        render={({
                          field,
                        }: {
                          field: ControllerRenderProps<
                            FormValues,
                            "numberOfUnits"
                          >;
                        }) => (
                          <FormItem
                            style={{
                              display:
                                filteredNumberOfUnitsOptions.length === 0
                                  ? "none"
                                  : "block",
                            }}
                          >
                            <FormLabel>Number of Units</FormLabel>
                            <Select
                              onValueChange={(value: string) =>
                                handleFieldChangeWithRecalculate(
                                  "numberOfUnits",
                                  value
                                )
                              }
                              value={field.value}
                              disabled={isNumberOfUnitsReadOnly}
                            >
                              <FormControl>
                                <SelectTrigger
                                  className={`h-8 text-sm bg-transparent border-0 border-b rounded-none px-0 focus:ring-0 focus:border-primary ${
                                    form.formState.errors.numberOfUnits
                                      ? "border-red-500 bg-red-50"
                                      : "border-gray-300"
                                  }`}
                                >
                                  <SelectValue placeholder="Select number of units" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {filteredNumberOfUnitsOptions.map(
                                  (option: DropdownOption) => (
                                    <SelectItem
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </SelectItem>
                                  )
                                )}
                              </SelectContent>
                            </Select>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="dti"
                        render={({
                          field,
                        }: {
                          field: ControllerRenderProps<FormValues, "dti">;
                        }) => (
                          <FormItem
                            style={{
                              display:
                                availableDtiOptions.length === 0 ||
                                watchedIncomeType?.includes("DSCR")
                                  ? "none"
                                  : "block",
                            }}
                          >
                            <FormLabel>DTI</FormLabel>
                            <Select
                              onValueChange={(value: string) =>
                                handleFieldChangeWithRecalculate("dti", value)
                              }
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger
                                  className={`h-8 text-sm bg-transparent border-0 border-b rounded-none px-0 focus:ring-0 focus:border-primary ${
                                    form.formState.errors.dti
                                      ? "border-red-500 bg-red-50"
                                      : "border-gray-300"
                                  }`}
                                >
                                  <SelectValue placeholder="Select DTI" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {availableDtiOptions.map(
                                  (option: DropdownOption) => (
                                    <SelectItem
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </SelectItem>
                                  )
                                )}
                              </SelectContent>
                            </Select>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="lockPeriod"
                        render={({
                          field,
                        }: {
                          field: ControllerRenderProps<
                            FormValues,
                            "lockPeriod"
                          >;
                        }) => (
                          <FormItem
                            style={{
                              display:
                                availableLockPeriodOptions.length === 0
                                  ? "none"
                                  : "block",
                            }}
                          >
                            <FormLabel>Lock Period</FormLabel>
                            <Select
                              onValueChange={(value: string) =>
                                handleFieldChangeWithRecalculate(
                                  "lockPeriod",
                                  value
                                )
                              }
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger
                                  className={`h-8 text-sm bg-transparent border-0 border-b rounded-none px-0 focus:ring-0 focus:border-primary ${
                                    form.formState.errors.lockPeriod
                                      ? "border-red-500 bg-red-50"
                                      : "border-gray-300"
                                  }`}
                                >
                                  <SelectValue placeholder="Select lock period" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {availableLockPeriodOptions.map(
                                  (option: DropdownOption) => (
                                    <SelectItem
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </SelectItem>
                                  )
                                )}
                              </SelectContent>
                            </Select>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />

                      {/* FHA: UFMIP Financed, VA: VA FF Financed, USDA: GFee Financed */}
                      {showFundingFeeFinanced && (
                        <FormField
                          control={form.control}
                          name="fundingFeeFinanced"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>UFMIP Financed</FormLabel>
                              <Select
                                onValueChange={(val) => {
                                  console.log(
                                    "Dropdown changed to (fundingFeeFinanced):",
                                    val
                                  );
                                  handleFieldChangeWithRecalculate(
                                    "fundingFeeFinanced",
                                    val
                                  );
                                  form.setValue("fundingFeeFinanced", val, {
                                    shouldDirty: true,
                                  });
                                }}
                                value={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger
                                    className={`h-8 text-sm bg-transparent border-0 border-b rounded-none px-0 focus:ring-0 focus:border-primary ${
                                      form.formState.errors.fundingFeeFinanced
                                        ? "border-red-500 bg-red-50"
                                        : "border-gray-300"
                                    }`}
                                  >
                                    <SelectValue placeholder="Select option" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="YES">Financed</SelectItem>
                                  <SelectItem value="NO">
                                    Not Financed
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage className="text-xs" />
                            </FormItem>
                          )}
                        />
                      )}
                      {showGuranteeFeeFinanced && (
                        <FormField
                          control={form.control}
                          name="guranteeFeeFinanced"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>
                                {form.getValues("mortgageType") === "VA"
                                  ? "VA FF Financed"
                                  : "GFee Financed"}
                              </FormLabel>
                              <Select
                                onValueChange={(val) => {
                                  console.log(
                                    "Dropdown changed to (guranteeFeeFinanced):",
                                    val
                                  );
                                  handleFieldChangeWithRecalculate(
                                    "guranteeFeeFinanced",
                                    val
                                  );
                                  form.setValue("guranteeFeeFinanced", val, {
                                    shouldDirty: true,
                                  });
                                }}
                                value={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger
                                    className={`h-8 text-sm bg-transparent border-0 border-b rounded-none px-0 focus:ring-0 focus:border-primary ${
                                      form.formState.errors.guranteeFeeFinanced
                                        ? "border-red-500 bg-red-50"
                                        : "border-gray-300"
                                    }`}
                                  >
                                    <SelectValue placeholder="Select option" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="YES">Financed</SelectItem>
                                  <SelectItem value="NO">
                                    Not Financed
                                  </SelectItem>
                                  {form.getValues("mortgageType") === "VA" && (
                                    <SelectItem value="EXEMPT">
                                      Exempt
                                    </SelectItem>
                                  )}
                                </SelectContent>
                              </Select>
                              <FormMessage className="text-xs" />
                            </FormItem>
                          )}
                        />
                      )}

                      {/* VA Service Type dropdown */}
                      {showveteranType && (
                        <FormField
                          control={form.control}
                          name="veteranType"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Veteran Type</FormLabel>
                              <Select
                                onValueChange={(value: string) =>
                                  handleFieldChangeWithRecalculate(
                                    "veteranType",
                                    value
                                  )
                                }
                                value={field.value || undefined}
                              >
                                <FormControl>
                                  <SelectTrigger
                                    className={`h-8 text-sm bg-transparent border-0 border-b rounded-none px-0 focus:ring-0 focus:border-primary ${
                                      form.formState.errors.veteranType
                                        ? "border-red-500 bg-red-50"
                                        : "border-gray-300"
                                    }`}
                                  >
                                    <SelectValue placeholder="Please select" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="Active_Or_Veteran">
                                    Active or Veteran
                                  </SelectItem>
                                  <SelectItem value="Reserves_Or_National_Guard">
                                    Reserves
                                  </SelectItem>
                                  <SelectItem value="National_Guard">
                                    National Guard
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage className="text-xs" />
                            </FormItem>
                          )}
                        />
                      )}

                      <FormField
                        control={form.control}
                        name="creditEvent"
                        render={({
                          field,
                        }: {
                          field: ControllerRenderProps<
                            FormValues,
                            "creditEvent"
                          >;
                        }) => (
                          <FormItem
                            style={{
                              display:
                                availableCreditEventOptions.length === 0
                                  ? "none"
                                  : "block",
                            }}
                          >
                            <FormLabel>Credit Event</FormLabel>
                            <Select
                              onValueChange={(value: string) =>
                                handleFieldChangeWithRecalculate(
                                  "creditEvent",
                                  value
                                )
                              }
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger
                                  className={`h-8 text-sm bg-transparent border-0 border-b rounded-none px-0 focus:ring-0 focus:border-primary ${
                                    form.formState.errors.creditEvent
                                      ? "border-red-500 bg-red-50"
                                      : "border-gray-300"
                                  }`}
                                >
                                  <SelectValue placeholder="Select credit event" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {availableCreditEventOptions.map(
                                  (option: DropdownOption) => (
                                    <SelectItem
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </SelectItem>
                                  )
                                )}
                              </SelectContent>
                            </Select>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />

                      {availablePrepaymentPenaltyOptions.length > 0 && (
                        <FormField
                          control={form.control}
                          name="prepaymentPenalty"
                          render={({
                            field,
                          }: {
                            field: ControllerRenderProps<
                              FormValues,
                              "prepaymentPenalty"
                            >;
                          }) => {
                            // Ensure the field value is valid for the current options
                            const validValue =
                              availablePrepaymentPenaltyOptions.find(
                                (opt) => opt.value === field.value
                              )?.value;
                            const displayValue =
                              validValue ||
                              availablePrepaymentPenaltyOptions.find(
                                (opt) => opt.default
                              )?.value ||
                              availablePrepaymentPenaltyOptions[0]?.value;

                            // Sync the form value if it's not valid
                            React.useEffect(() => {
                              if (
                                !validValue &&
                                displayValue &&
                                field.value !== displayValue
                              ) {
                                field.onChange(displayValue);
                              }
                            }, [validValue, displayValue, field]);

                            console.log("Prepayment Penalty Form Field:", {
                              fieldValue: field.value,
                              validValue,
                              displayValue,
                              availableOptions:
                                availablePrepaymentPenaltyOptions,
                              selectedOption:
                                availablePrepaymentPenaltyOptions.find(
                                  (opt) => opt.value === field.value
                                ),
                            });

                            return (
                              <FormItem
                                style={{
                                  display:
                                    availablePrepaymentPenaltyOptions.length ===
                                    0
                                      ? "none"
                                      : "block",
                                }}
                              >
                                <FormLabel>Prepayment Penalty</FormLabel>
                                <Select
                                  key={`prepayment-penalty-${availablePrepaymentPenaltyOptions
                                    .map((opt) => opt.value)
                                    .join("-")}`}
                                  onValueChange={(value: string) => {
                                    handleFieldChangeWithRecalculate(
                                      "prepaymentPenalty",
                                      value
                                    );
                                    // Ensure the form value is updated immediately
                                    form.setValue("prepaymentPenalty", value, {
                                      shouldValidate: true,
                                      shouldDirty: true,
                                    });
                                  }}
                                  value={displayValue}
                                >
                                  <FormControl>
                                    <SelectTrigger
                                      className={`h-8 text-sm bg-transparent border-0 border-b rounded-none px-0 focus:ring-0 focus:border-primary ${
                                        form.formState.errors.prepaymentPenalty
                                          ? "border-red-500 bg-red-50"
                                          : "border-gray-300"
                                      }`}
                                    >
                                      <SelectValue placeholder="Select prepayment penalty" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {availablePrepaymentPenaltyOptions.map(
                                      (option: DropdownOption) => (
                                        <SelectItem
                                          key={option.value}
                                          value={option.value}
                                        >
                                          {option.label}
                                        </SelectItem>
                                      )
                                    )}
                                  </SelectContent>
                                </Select>
                                <FormMessage className="text-xs" />
                              </FormItem>
                            );
                          }}
                        />
                      )}

                      <FormField
                        control={form.control}
                        name="state"
                        render={({
                          field,
                        }: {
                          field: ControllerRenderProps<FormValues, "state">;
                        }) => (
                          <FormItem>
                            <FormLabel>State</FormLabel>
                            <Select
                              onValueChange={(value: string) => {
                                handleFieldChangeWithRecalculate(
                                  "state",
                                  value
                                );
                                // Zip code will be automatically updated by the useEffect
                              }}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger
                                  className={`h-8 text-sm bg-transparent border-0 border-b rounded-none px-0 focus:ring-0 focus:border-primary ${
                                    form.formState.errors.state
                                      ? "border-red-500 bg-red-50"
                                      : "border-gray-300"
                                  }`}
                                >
                                  <SelectValue placeholder="Select state" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {getStateOptions(
                                  watchedIncomeType,
                                  watchedProgram,
                                  watchedOccupancy
                                ).map((option: DropdownOption) => (
                                  <SelectItem
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage className="text-xs" />
                          </FormItem>
                        )}
                      />

                      {/* Broker Comp dropdown after State */}
                      {!watchedIncomeType?.includes("DSCR") &&
                        !watchedIncomeType?.includes("First Time") && (
                          <FormField
                            control={form.control}
                            name="brokerCompensation"
                            render={({
                              field,
                            }: {
                              field: ControllerRenderProps<
                                FormValues,
                                "brokerCompensation"
                              >;
                            }) => (
                              <FormItem>
                                <FormLabel>Broker Compensation</FormLabel>
                                <Select
                                  value={field.value}
                                  onValueChange={(value: string) => {
                                    handleFieldChange(
                                      "brokerCompensation",
                                      value
                                    );
                                    if (value === "LPC") {
                                      setShowBrokerCompAmount(true);

                                      // Check if we have a last LPC value from session storage
                                      const lastLPCValue = getLastLPCValue();

                                      if (lastLPCValue !== null) {
                                        // Use the last LPC value, but validate it against current constraints
                                        const loanAmount =
                                          form.getValues("loanAmount");
                                        const maxAmount =
                                          brokerCompensationRange.maxAmount;
                                        const max = brokerCompensationRange.max;
                                        const min = brokerCompensationRange.min;

                                        // Calculate the capped percentage (max USD amount)
                                        const cappedPercent =
                                          loanAmount > 0
                                            ? (maxAmount / loanAmount) * 100
                                            : max;

                                        // Use the last value if it's within valid range, otherwise use max
                                        const validValue = Math.min(
                                          Math.max(lastLPCValue, min),
                                          Math.min(max, cappedPercent)
                                        );

                                        handleFieldChange(
                                          "brokerCompensationValue",
                                          Number(validValue.toFixed(3))
                                        );
                                      } else {
                                        // No last value, calculate and set the maximum value
                                        const loanAmount =
                                          form.getValues("loanAmount");
                                        const maxAmount =
                                          brokerCompensationRange.maxAmount;
                                        const max = brokerCompensationRange.max;
                                        const step =
                                          brokerCompensationRange.step;
                                        const min = brokerCompensationRange.min;

                                        // Calculate the capped percentage (max USD amount)
                                        const cappedPercent =
                                          loanAmount > 0
                                            ? (maxAmount / loanAmount) * 100
                                            : max;

                                        // Use the smaller of max or cappedPercent, which can be a custom value
                                        const maxValue = Math.min(
                                          max,
                                          cappedPercent
                                        );
                                        handleFieldChange(
                                          "brokerCompensationValue",
                                          Number(maxValue.toFixed(3))
                                        );
                                      }
                                    } else {
                                      setShowBrokerCompAmount(false);
                                      handleFieldChange(
                                        "brokerCompensationValue",
                                        0
                                      );
                                    }
                                  }}
                                >
                                  <FormControl>
                                    <SelectTrigger
                                      className={`h-8 text-sm bg-transparent border-0 border-b rounded-none px-0 focus:ring-0 focus:border-primary ${
                                        form.formState.errors.brokerCompensation
                                          ? "border-red-500 bg-red-50"
                                          : "border-gray-300"
                                      }`}
                                    >
                                      <SelectValue placeholder="Select type" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    <SelectItem value="BPC">BPC</SelectItem>
                                    {getBrokerCompensationRange(watchedProgram)
                                      .min !== undefined && (
                                      <SelectItem value="LPC">LPC</SelectItem>
                                    )}
                                  </SelectContent>
                                </Select>
                                <FormMessage className="text-xs" />
                                {/* Show amount input if LPC is selected */}
                                {showBrokerCompAmount && (
                                  <FormField
                                    control={form.control}
                                    name="brokerCompensationValue"
                                    render={({
                                      field,
                                    }: {
                                      field: ControllerRenderProps<
                                        FormValues,
                                        "brokerCompensationValue"
                                      >;
                                    }) => {
                                      // --- Begin: Fix for capping bug and step alignment ---
                                      const loanAmount =
                                        form.getValues("loanAmount");
                                      const maxAmount =
                                        brokerCompensationRange.maxAmount;
                                      const step = brokerCompensationRange.step;
                                      const min = brokerCompensationRange.min;
                                      const max = brokerCompensationRange.max;
                                      const cappedPercent =
                                        loanAmount > 0
                                          ? (maxAmount / loanAmount) * 100
                                          : max;
                                      // Find the largest valid step below cappedPercent
                                      function getPrevStep(val: number) {
                                        // Find the largest step-aligned value below val
                                        const stepsBelow = Math.floor(
                                          (val - min) / step
                                        );
                                        return parseFloat(
                                          (min + stepsBelow * step).toFixed(3)
                                        );
                                      }
                                      // If current value would yield more than maxAmount, set to cappedPercent (rounded to 3 decimals)
                                      useEffect(() => {
                                        const selectedPercent = Number(
                                          field.value
                                        );
                                        const calculatedAmount =
                                          (selectedPercent / 100) * loanAmount;
                                        if (calculatedAmount > maxAmount) {
                                          // Only set if not already capped (avoid infinite loop)
                                          if (
                                            Math.abs(
                                              selectedPercent - cappedPercent
                                            ) > 0.0001
                                          ) {
                                            field.onChange(
                                              Number(cappedPercent.toFixed(3))
                                            );
                                          }
                                        }
                                      }, [loanAmount, field.value]);
                                      // --- End: Fix for capping bug and step alignment ---
                                      return (
                                        <FormItem className="mt-2">
                                          <div className="relative w-full">
                                            <div className="flex items-center">
                                              <span
                                                className="w-8 text-center select-none"
                                                style={{
                                                  display: "inline-block",
                                                  minWidth: "4.5rem",
                                                }}
                                              >
                                                {(() => {
                                                  // If the selected % would yield more than $20,000, show the % for $20,000
                                                  const selectedPercent =
                                                    Number(field.value);
                                                  const calculatedAmount =
                                                    (selectedPercent / 100) *
                                                    loanAmount;
                                                  if (
                                                    calculatedAmount > maxAmount
                                                  ) {
                                                    return (
                                                      cappedPercent.toLocaleString(
                                                        undefined,
                                                        {
                                                          minimumFractionDigits: 3,
                                                          maximumFractionDigits: 3,
                                                        }
                                                      ) + " %"
                                                    );
                                                  } else {
                                                    return (
                                                      selectedPercent.toLocaleString(
                                                        undefined,
                                                        {
                                                          minimumFractionDigits: 3,
                                                          maximumFractionDigits: 3,
                                                        }
                                                      ) + " %"
                                                    );
                                                  }
                                                })()}
                                              </span>
                                              <div
                                                className="flex flex-col ml-1"
                                                style={{ lineHeight: 1 }}
                                              >
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    const currentValue = Number(
                                                      field.value
                                                    );
                                                    let newValue = Math.min(
                                                      currentValue + step,
                                                      max
                                                    );
                                                    // If newValue would yield more than maxAmount, cap it
                                                    if (
                                                      (newValue / 100) *
                                                        loanAmount >
                                                      maxAmount
                                                    ) {
                                                      newValue = Number(
                                                        cappedPercent.toFixed(3)
                                                      );
                                                    }
                                                    field.onChange(newValue);
                                                  }}
                                                  disabled={
                                                    Number(field.value) >=
                                                      max ||
                                                    (Number(field.value) /
                                                      100) *
                                                      loanAmount >=
                                                      maxAmount
                                                  }
                                                  className="px-1 py-0 text-gray-500 hover:text-gray-700 focus:outline-none"
                                                >
                                                  ▲
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    const currentValue = Number(
                                                      field.value
                                                    );
                                                    // Always find the next valid step below current value
                                                    let prevStep = getPrevStep(
                                                      currentValue - 0.0001
                                                    ); // subtract a tiny bit to avoid rounding issues
                                                    // Clamp to min
                                                    if (prevStep < min)
                                                      prevStep = min;
                                                    field.onChange(prevStep);
                                                  }}
                                                  className="px-1 py-0 text-gray-500 hover:text-gray-700 focus:outline-none"
                                                >
                                                  ▼
                                                </button>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="text-xs text-gray-500 mt-1">
                                            <div className="flex justify-between">
                                              <span>Amount:</span>
                                              <span>
                                                {(() => {
                                                  // If the selected % would yield more than $20,000, cap the displayed amount at $20,000
                                                  const selectedPercent =
                                                    Number(field.value);
                                                  const calculatedAmount =
                                                    (selectedPercent / 100) *
                                                    loanAmount;
                                                  if (
                                                    calculatedAmount > maxAmount
                                                  ) {
                                                    return maxAmount.toLocaleString(
                                                      "en-US",
                                                      {
                                                        style: "currency",
                                                        currency: "USD",
                                                      }
                                                    );
                                                  } else {
                                                    return calculatedAmount.toLocaleString(
                                                      "en-US",
                                                      {
                                                        style: "currency",
                                                        currency: "USD",
                                                      }
                                                    );
                                                  }
                                                })()}
                                              </span>
                                            </div>
                                            <div className="flex justify-between">
                                              <span>Range:</span>
                                              <span>
                                                {min.toLocaleString(undefined, {
                                                  maximumFractionDigits: 3,
                                                })}
                                                % -
                                                {max.toLocaleString(undefined, {
                                                  maximumFractionDigits: 3,
                                                })}
                                                %
                                              </span>
                                            </div>
                                            <div className="flex justify-between">
                                              <span>Max:</span>
                                              <span>
                                                {maxAmount.toLocaleString(
                                                  "en-US",
                                                  {
                                                    style: "currency",
                                                    currency: "USD",
                                                  }
                                                )}
                                              </span>
                                            </div>
                                          </div>
                                          <FormMessage className="text-xs" />
                                        </FormItem>
                                      );
                                    }}
                                  />
                                )}
                              </FormItem>
                            )}
                          />
                        )}

                      {/* Investor Experience dropdown */}
                      {showInvestorExperience && (
                        <FormField
                          control={form.control}
                          name="investorExperience"
                          render={({
                            field,
                          }: {
                            field: ControllerRenderProps<
                              FormValues,
                              "investorExperience"
                            >;
                          }) => (
                            <FormItem>
                              <FormLabel>Investor Experience</FormLabel>
                              <Select
                                value={field.value}
                                onValueChange={(value: string) => {
                                  handleFieldChange(
                                    "investorExperience",
                                    value
                                  );
                                }}
                              >
                                <FormControl>
                                  <SelectTrigger
                                    className={`h-8 text-sm bg-transparent border-0 border-b rounded-none px-0 focus:ring-0 focus:border-primary ${
                                      form.formState.errors.investorExperience
                                        ? "border-red-500 bg-red-50"
                                        : "border-gray-300"
                                    }`}
                                  >
                                    <SelectValue />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="false">
                                    Experienced Investor
                                  </SelectItem>
                                  <SelectItem value="true">
                                    First Time Investor
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage className="text-xs" />
                            </FormItem>
                          )}
                        />
                      )}

                      {/* Rental Term dropdown - only show for DSCR income types when occupancy is Investment */}
                      {watchedIncomeType?.startsWith("DSCR") &&
                        watchedOccupancy === "Investment" && (
                          <FormField
                            control={form.control}
                            name="rentalTerm"
                            render={({
                              field,
                            }: {
                              field: ControllerRenderProps<
                                FormValues,
                                "rentalTerm"
                              >;
                            }) => (
                              <FormItem>
                                <FormLabel>Rental Term</FormLabel>
                                <Select
                                  onValueChange={(value: string) =>
                                    handleFieldChangeWithRecalculate(
                                      "rentalTerm",
                                      value
                                    )
                                  }
                                  value={field.value}
                                >
                                  <FormControl>
                                    <SelectTrigger
                                      className={`h-8 text-sm bg-transparent border-0 border-b rounded-none px-0 focus:ring-0 focus:border-primary ${
                                        form.formState.errors.rentalTerm
                                          ? "border-red-500 bg-red-50"
                                          : "border-gray-300"
                                      }`}
                                    >
                                      <SelectValue />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {availableRentalTermOptions.map(
                                      (option: DropdownOption) => (
                                        <SelectItem
                                          key={option.value}
                                          value={option.value}
                                        >
                                          {option.label}
                                        </SelectItem>
                                      )
                                    )}
                                  </SelectContent>
                                </Select>
                                <FormMessage className="text-xs" />
                              </FormItem>
                            )}
                          />
                        )}

                      {/* DSCR Value input - only show for DSCR income types */}
                      {(watchedIncomeType === "DSCR" ||
                        watchedIncomeType === "DSCR-AssetUtilization") && (
                        <FormField
                          control={form.control}
                          name="dscrValue"
                          render={({
                            field,
                          }: {
                            field: ControllerRenderProps<
                              FormValues,
                              "dscrValue"
                            >;
                          }) => (
                            <FormItem>
                              <FormLabel>DSCR Value</FormLabel>
                              <FormControl>
                                <Input
                                  type="text"
                                  value={field.value || ""}
                                  onChange={(e) => {
                                    // If income type is "DSCR-AssetUtilization", don't allow changes
                                    if (
                                      watchedIncomeType ===
                                      "DSCR-AssetUtilization"
                                    ) {
                                      return;
                                    }

                                    const inputValue = e.target.value;

                                    // Only allow digits and decimal point
                                    if (!/^[0-9.]*$/.test(inputValue)) {
                                      return; // Don't update if invalid characters
                                    }

                                    // Prevent multiple decimal points
                                    const decimalCount = (
                                      inputValue.match(/\./g) || []
                                    ).length;
                                    if (decimalCount > 1) {
                                      return; // Don't update if multiple decimal points
                                    }

                                    handleFieldChangeNoAutoCalc(
                                      "dscrValue",
                                      inputValue
                                    );
                                  }}
                                  onBlur={(e) => {
                                    // If income type is "DSCR-AssetUtilization", don't allow changes
                                    if (
                                      watchedIncomeType ===
                                      "DSCR-AssetUtilization"
                                    ) {
                                      return;
                                    }

                                    const inputValue = e.target.value.trim();

                                    // Allow empty value
                                    if (inputValue === "") {
                                      form.clearErrors("dscrValue");
                                      handleFieldChange("dscrValue", undefined);
                                      handleFieldBlur("dscrValue");
                                      return;
                                    }

                                    // Check if input is a valid number (only digits and decimal point allowed)
                                    if (!/^[0-9.]*$/.test(inputValue)) {
                                      // Invalid characters - reset to previous valid value
                                      handleFieldChange(
                                        "dscrValue",
                                        field.value
                                      );
                                      handleFieldBlur("dscrValue");
                                      return;
                                    }

                                    // Check for multiple decimal points
                                    const decimalCount = (
                                      inputValue.match(/\./g) || []
                                    ).length;
                                    if (decimalCount > 1) {
                                      // Multiple decimal points - reset to previous valid value
                                      handleFieldChange(
                                        "dscrValue",
                                        field.value
                                      );
                                      handleFieldBlur("dscrValue");
                                      return;
                                    }

                                    // Parse the number
                                    const numericValue = parseFloat(inputValue);

                                    if (isNaN(numericValue)) {
                                      // Not a valid number - reset to previous valid value
                                      handleFieldChange(
                                        "dscrValue",
                                        field.value
                                      );
                                      handleFieldBlur("dscrValue");
                                      return;
                                    }

                                    // Check range (0-10)
                                    if (numericValue < 0 || numericValue > 10) {
                                      // Out of range - set error and don't update value
                                      form.setError("dscrValue", {
                                        type: "manual",
                                        message:
                                          "DSCR Value must be between 0 and 10",
                                      });
                                      handleFieldBlur("dscrValue");
                                      return;
                                    }

                                    // Round to 2 decimal places if needed
                                    const roundedValue =
                                      Math.round(numericValue * 100) / 100;

                                    // Clear any existing errors
                                    form.clearErrors("dscrValue");

                                    // Update with the validated and rounded value
                                    handleFieldChange(
                                      "dscrValue",
                                      roundedValue
                                    );
                                    handleFieldBlur("dscrValue");
                                  }}
                                  readOnly={
                                    watchedIncomeType ===
                                    "DSCR-AssetUtilization"
                                  }
                                  className={`h-8 text-sm bg-transparent border-0 border-b rounded-none px-0 focus:ring-0 focus:border-primary ${
                                    form.formState.errors.dscrValue
                                      ? "border-red-500 bg-red-50"
                                      : "border-gray-300"
                                  } ${
                                    watchedIncomeType ===
                                    "DSCR-AssetUtilization"
                                      ? "bg-gray-100 cursor-not-allowed text-gray-500 disabled"
                                      : ""
                                  }`}
                                  placeholder=""
                                />
                              </FormControl>
                              <FormMessage className="text-xs" />
                            </FormItem>
                          )}
                        />
                      )}

                      <div className="col-span-2 sm:col-span-3">
                        <div className="flex flex-row flex-wrap justify-between gap-x-6 gap-y-2 mb-2">
                          {showLenderFeeBuyOut && (
                            <FormField
                              control={form.control}
                              name="lenderFeeBuyOut"
                              render={({ field }) => (
                                <FormItem className="flex flex-col space-y-2 min-h-[60px] flex-1">
                                  <div className="flex items-start space-x-2">
                                    <FormControl>
                                      <Checkbox
                                        checked={field.value}
                                        onCheckedChange={(checked) =>
                                          handleFieldChange(
                                            "lenderFeeBuyOut",
                                            checked === true
                                          )
                                        }
                                      />
                                    </FormControl>
                                    <FormLabel>Lender Fee Buyout</FormLabel>
                                  </div>
                                  <div className="min-h-[20px]">
                                    {/* Removed underwriting fee percentage display */}
                                  </div>
                                </FormItem>
                              )}
                            />
                          )}
                          {showEscrowWaiver && (
                            <FormField
                              control={form.control}
                              name="escrowWaiver"
                              render={({ field }) => (
                                <FormItem className="flex flex-col space-y-2 min-h-[60px] flex-1">
                                  <div className="flex items-start space-x-2">
                                    <FormControl>
                                      <Checkbox
                                        checked={field.value}
                                        onCheckedChange={(checked) =>
                                          handleFieldChange(
                                            "escrowWaiver",
                                            checked === true
                                          )
                                        }
                                      />
                                    </FormControl>
                                    <FormLabel>Escrow Waiver</FormLabel>
                                  </div>
                                  <div className="min-h-[20px]">
                                    {/* Reserved for future label */}
                                  </div>
                                </FormItem>
                              )}
                            />
                          )}
                          {/* FTHB: Non-QM + DSCR (NONI); hidden for SuperNONI Asset Utilization */}
                          {showFirstTimeHomeBuyer &&
                            watchedIncomeType !== "DSCR-AssetUtilization" && (
                              <FormField
                                control={form.control}
                                name="firstTimeHomeBuyer"
                                render={({ field }) => (
                                  <FormItem className="flex flex-col space-y-2 min-h-[60px] flex-1">
                                    <div className="flex items-start space-x-2">
                                      <FormControl>
                                        <Checkbox
                                          checked={field.value}
                                          onCheckedChange={(checked) =>
                                            handleFieldChange(
                                              "firstTimeHomeBuyer",
                                              checked === true
                                            )
                                          }
                                        />
                                      </FormControl>
                                      <FormLabel>
                                        First Time Home Buyer
                                      </FormLabel>
                                    </div>
                                    <div className="min-h-[20px]">
                                      {/* Reserved for future label */}
                                    </div>
                                  </FormItem>
                                )}
                              />
                            )}

                          {showSubFinancing && (
                            <FormField
                              control={form.control}
                              name="subFinancing"
                              render={({ field }) => (
                                <FormItem className="flex flex-col space-y-2 min-h-[60px] flex-1">
                                  <div className="flex items-start space-x-2">
                                    <FormControl>
                                      <Checkbox
                                        checked={field.value}
                                        onCheckedChange={(checked) =>
                                          handleFieldChange(
                                            "subFinancing",
                                            checked === true
                                          )
                                        }
                                      />
                                    </FormControl>
                                    <FormLabel>Sub Financing</FormLabel>
                                  </div>
                                  <div className="min-h-[20px]">
                                    {/* Reserved for future label */}
                                  </div>
                                </FormItem>
                              )}
                            />
                          )}

                          {/* ACH Waiver checkbox - only show for DSCR income types */}
                          {(watchedIncomeType === "DSCR" ||
                            watchedIncomeType === "DSCR-AssetUtilization") && (
                            <FormField
                              control={form.control}
                              name="achWaiver"
                              render={({ field }) => (
                                <FormItem className="flex flex-col space-y-2 min-h-[60px] flex-1">
                                  <div className="flex items-start space-x-2">
                                    <FormControl>
                                      <Checkbox
                                        checked={field.value}
                                        onCheckedChange={(checked) =>
                                          handleFieldChange(
                                            "achWaiver",
                                            checked === true
                                          )
                                        }
                                      />
                                    </FormControl>
                                    <FormLabel>ACH Waiver</FormLabel>
                                  </div>
                                  <div className="min-h-[20px]">
                                    {/* Reserved for future label */}
                                  </div>
                                </FormItem>
                              )}
                            />
                          )}
                          {showInterestOnly && (
                            <FormField
                              control={form.control}
                              name="interestOnly"
                              render={({ field }) => (
                                <FormItem className="flex flex-col space-y-2 min-h-[60px] flex-1">
                                  <div className="flex items-start space-x-2">
                                    <FormControl>
                                      <Checkbox
                                        checked={field.value}
                                        onCheckedChange={(checked) =>
                                          !interestOnlyReadOnly &&
                                          handleFieldChange(
                                            "interestOnly",
                                            checked === true
                                          )
                                        }
                                        disabled={interestOnlyReadOnly}
                                      />
                                    </FormControl>
                                    <FormLabel>Interest Only</FormLabel>
                                  </div>
                                  <div className="min-h-[20px]">
                                    {/* Reserved for future label */}
                                  </div>
                                </FormItem>
                              )}
                            />
                          )}
                          {showEntitlementUsage && (
                            <FormField
                              control={form.control}
                              name="entitlementUsage"
                              render={({ field }) => (
                                <FormItem className="flex flex-col space-y-2 min-h-[60px] flex-1">
                                  <div className="flex items-start space-x-2">
                                    <FormControl>
                                      <Checkbox
                                        checked={field.value}
                                        onCheckedChange={(checked) =>
                                          handleFieldChange(
                                            "entitlementUsage",
                                            checked === true
                                          )
                                        }
                                      />
                                    </FormControl>
                                    <FormLabel>Subsequent Use</FormLabel>
                                  </div>
                                  <div className="min-h-[20px]">
                                    {/* Reserved for future label */}
                                  </div>
                                </FormItem>
                              )}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      {currentProduct && (
        <SharePopup
          isOpen={isSharePopupOpen}
          onClose={() => setIsSharePopupOpen(false)}
          loanInfo={{
            program:
              displayProduct.alternativeProgram ||
              `${displayProduct.loanType || watchedValues.program} ${
                displayProduct.amortizationType || "Fixed"
              }`,
            rate: displayProduct.finalRate,
            monthlyPayment: monthlyPayment,
            lenderCredit:
              displayProduct.adjustments.find(
                (adj: Adjustment) => adj.name === "Price"
              )?.value || 0,
            price:
              typeof displayProduct.price === "number"
                ? displayProduct.price.toFixed(3)
                : "-",
            fico: watchedValues.fico,
            ltv: watchedValues.ltv,
            loanAmount: watchedValues.loanAmount,
            citizenship: watchedValues.citizenship,
            incomeType: watchedValues.incomeType,
            occupancy: watchedValues.occupancy,
            purpose: watchedValues.purpose,
            amortizationType: displayProduct.amortizationType,
            prepaymentPenalty: watchedValues.prepaymentPenalty,
            creditEvent: watchedValues.creditEvent,
            propertyType: watchedValues.propertyType,
            brokerCompensation: watchedValues.brokerCompensation
              ? "BPC"
              : "None",
            mortgageHistory: watchedValues.mortgageHistory,
            state: watchedValues.state,
            disqualified: displayProduct.disqualified,
            disqualifyAdjustments: displayProduct.disqualifyAdjustments,
            allAdjustments: displayProduct.allAdjustments,
            rateStack: currentResults.map((result) => ({
              rate: result.finalRate,
              price: result.price,
              monthlyPayment:
                result.monthlyPayment?.monthlyPI || result.totalPayment,
            })),
          }}
        />
      )}
      {/* Adjustments Popup */}
      {currentProduct &&
        displayProduct.allAdjustments &&
        displayProduct.allAdjustments.length > 0 && (
          <Dialog
            open={isAdjustmentsPopupOpen}
            onOpenChange={setIsAdjustmentsPopupOpen}
          >
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Adjustment Details</DialogTitle>
                <DialogDescription>
                  Detailed breakdown of all adjustments applied to this loan
                  program
                </DialogDescription>
              </DialogHeader>
              <div className="max-h-96 overflow-y-auto">
                <div className="space-y-2">
                  {displayProduct.allAdjustments.map((adjustment, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded text-sm">
                      <div className="font-medium text-gray-900">
                        {adjustment.name}
                      </div>
                      <div className="text-gray-600 text-xs mt-1">
                        Type: {adjustment.type} | Value:{" "}
                        {adjustment.value.toFixed(3)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => setIsAdjustmentsPopupOpen(false)}>
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

      {/* Rate Stack Popup */}
      {currentProgram && currentResults.length > 0 && (
        <Dialog
          open={isRateStackPopupOpen}
          onOpenChange={setIsRateStackPopupOpen}
        >
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-center text-xl font-bold">
                {currentProgram.programName}
              </DialogTitle>
            </DialogHeader>
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-center py-3 font-semibold">Rate</th>
                    <th className="text-center py-3 font-semibold">Price</th>
                    <th className="text-center py-3 font-semibold">
                      Monthly Payment
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentResults.map((result, index) => (
                    <tr
                      key={index}
                      className={`border-b cursor-pointer hover:bg-gray-50 ${
                        index === currentRateIndex ? "bg-blue-50" : ""
                      }`}
                      onClick={() => {
                        setCurrentRateIndex(index);
                        setIsRateStackPopupOpen(false);
                      }}
                    >
                      <td className="text-center py-3">
                        {result.finalRate.toFixed(3)}%
                      </td>
                      <td className="text-center py-3">
                        {result.price.toFixed(3)}
                      </td>
                      <td className="text-center py-3">
                        {new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(
                          result.monthlyPayment?.monthlyPI ||
                            result.totalPayment
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <DialogFooter>
              <Button onClick={() => setIsRateStackPopupOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {/* Debug Section - only show when debugInfo query param is true */}
      {debugInfoEnabled && (
        <div className="mt-8">
          <button
            type="button"
            className="px-4 py-2 bg-gray-200 rounded text-xs mb-2"
            onClick={() => setShowDebug((v) => !v)}
          >
            {showDebug ? "Hide Debug Info" : "Show Debug Info"}
          </button>
          {showDebug && (
            <div className="bg-gray-100 p-4 rounded text-xs overflow-x-auto">
              <div className="mb-2">
                <button
                  type="button"
                  className="font-bold underline text-left w-full"
                  onClick={() => setShowReq((v) => !v)}
                >
                  {showReq ? "▼" : "►"} API Request
                </button>
                {showReq && (
                  <pre className="bg-white p-2 rounded border mt-1 mb-2">
                    <code>{JSON.stringify(debugRequest, null, 2)}</code>
                  </pre>
                )}
              </div>
              <div className="mb-2">
                <button
                  type="button"
                  className="font-bold underline text-left w-full"
                  onClick={() => setShowResp((v) => !v)}
                >
                  {showResp ? "▼" : "►"} Raw API Response
                </button>
                {showResp && (
                  <pre className="bg-white p-2 rounded border mt-1 mb-2">
                    <code>{JSON.stringify(debugResponse, null, 2)}</code>
                  </pre>
                )}
              </div>
              <div className="mb-2">
                <button
                  type="button"
                  className="font-bold underline text-left w-full"
                  onClick={() => setShowMapped((v) => !v)}
                >
                  {showMapped ? "▼" : "►"} Mapped Results (after
                  filtering/adjustment)
                </button>
                {showMapped && (
                  <pre className="bg-white p-2 rounded border mt-1 mb-2">
                    <code>
                      {debugMapped && Object.keys(debugMapped).length > 0
                        ? JSON.stringify(debugMapped, null, 2)
                        : apiError
                        ? JSON.stringify({ error: apiError }, null, 2)
                        : "No mapped results or error info available."}
                    </code>
                  </pre>
                )}
              </div>
              <div className="mb-2">
                <button
                  type="button"
                  className="font-bold underline text-left w-full"
                  onClick={() => setShowCalc((v) => !v)}
                >
                  {showCalc ? "▼" : "►"} Calculations
                </button>
                {showCalc && (
                  <pre className="bg-white p-2 rounded border mt-1 mb-2">
                    <code>
                      {JSON.stringify(
                        {
                          underwritingFeePercentage:
                            form.getValues().underwritingFeePercentage,
                          lenderFeeBuyOut: form.getValues().lenderFeeBuyOut,
                          brokerCompensation:
                            form.getValues().brokerCompensation,
                          brokerCompensationValue:
                            form.getValues().brokerCompensationValue,
                          // Show API error if present
                          ...(apiError ? { apiError } : {}),
                        },
                        null,
                        2
                      )}
                    </code>
                  </pre>
                )}
              </div>
              {/* Error Details Section */}
              {(debugMapped?.error || apiError) && (
                <div className="mb-2">
                  <button
                    type="button"
                    className="font-bold underline text-left w-full"
                    onClick={() => setShowCalc((v) => !v)}
                  >
                    {showCalc ? "▼" : "►"} Error Details
                  </button>
                  {showCalc && (
                    <pre className="bg-white p-2 rounded border mt-1 mb-2 overflow-x-auto break-words whitespace-pre-wrap">
                      <code>
                        {debugMapped?.error
                          ? JSON.stringify(debugMapped.error, null, 2)
                          : apiError
                          ? JSON.stringify({ error: apiError }, null, 2)
                          : "No error details available."}
                      </code>
                    </pre>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PricingCalculator;
